import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../utils/api';

type TabId = 'pipeline-runs' | 'drafts' | 'weekly' | 'top-picks';

interface JobResponse {
  public_id: string;
  /** "email" (legacy default) | "discovery" */
  kind: 'email' | 'discovery';
  /** Discovery jobs only — null on email jobs (week is parsed from the .eml). */
  week_iso: string | null;
  /** Discovery jobs only — list of source slugs (arxiv/ssrn/nber). */
  sources: string[] | null;
  /** Email jobs only — null on discovery. */
  source_email_public_id: string | null;
  source_email_filename: string | null;
  status: string;
  current_step: string | null;
  progress_pct: number;
  papers_total: number;
  papers_enriched: number;
  error_message: string | null;
  draft_insight_public_ids: string[];
  started_at: string | null;
  completed_at: string | null;
  created_at: string | null;
}

interface JobWithTimings extends JobResponse {
  is_test_run: boolean;
  sample_size: number | null;
  stage_timings: Record<string, number>;
  parent_job_public_id: string | null;
}

interface AuditEntry {
  action: string;
  actor_public_id: string;
  changed_fields: Record<string, { old: unknown; new: unknown }>;
  created_at: string;
}

interface InsightSourceEmail {
  public_id: string;
  publication_slug: string | null;
  display_name: string | null;
  issue_date: string | null;
  subject: string | null;
  paper_count: number;
}

// v3 — confidence-bucket curator verdict surfaced on every row.
interface CurationVerdictView {
  decision: 'auto_publish' | 'auto_publish_flag' | 'hold_for_review' | 'auto_dismiss' | null;
  confidence: number | null;
  gates_passed: string[];
  gates_failed: string[];
  reason: string;
}

interface InsightRow {
  public_id: string;
  week_iso: string;
  paper_title: string;
  paper_url: string;
  source_type: string | null;
  source_publications: Array<{ slug?: string; display_name?: string }>;
  corroborated: boolean;
  propalgos_relevance: string;
  feature_tags: string[] | null;
  summary_md: string | null;
  classification: string | null;
  fidelity_score: number | null;
  recommended_gpu_tier: string | null;
  actionability_json: unknown;
  code_references: unknown[] | null;
  paper_published_at: string | null;
  status: string;
  // v3 fields — optional for back-compat with rows ingested before the
  // schema migration. UI must guard with `?? defaults`.
  flagged_for_review?: boolean;
  curation_verdict?: CurationVerdictView | null;
  infographic_url?: string | null;
  // v3.3 — cross-week dedup. Non-empty list = this paper_url was
  // previously published in those weeks. Curator force-routed to
  // Bucket C; admin can override via manual publish but it'll never
  // be a Top Pick.
  previously_shown_weeks?: string[];
  created_at: string;
  updated_at: string;
  audit_log: AuditEntry[];
  source_email: InsightSourceEmail | null;
}

// v3 — Bedrock-generated edit suggestion for the Inbox tab.
interface SuggestionRow {
  public_id: string;
  insight_public_id: string;
  field_name: 'summary_md' | 'feature_tags' | 'classification' | 'headline';
  current_value: unknown;
  proposed_value: unknown;
  rationale: string;
  generated_at: string;
  applied_at: string | null;
  dismissed_at: string | null;
  source: string;
}

interface CurationStatsResponse {
  week_iso: string;
  stats: {
    auto_published: number;
    flagged_for_review: number;
    inbox: number;
    auto_dismissed: number;
    top_picks: Array<{
      public_id: string;
      headline: string;
      classification: string;
      paper_url: string;
      infographic_s3_key: string | null;
    }>;
    weekly_summary_published: boolean;
  };
  markdown_body: string;
}

interface ReadinessResponse {
  week_iso: string;
  published_count: number;
  draft_count: number;
  draft_public_ids: string[];
  can_generate: boolean;
  recommendation: string | null;
}

interface SummaryStageEvent {
  step: string;
  outcome: 'ok' | 'error' | 'skip';
  started_at: string | null;
  finished_at: string | null;
  detail: string | null;
  meta: Record<string, unknown>;
}

interface SummaryAttempt {
  attempt: number;
  model_id: string;
  started_at: string | null;
  finished_at: string | null;
  outcome: 'success' | 'invoke_error' | 'json_parse' | 'schema' | 'intro_quality';
  error_detail: string | null;
  tokens_input: number;
  tokens_output: number;
  cost_cents: number;
}

interface WeeklySummaryJob {
  public_id: string;
  week_iso: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  current_step: string | null;
  progress_pct: number;
  input_paper_count: number | null;
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost_cents: number;
  stage_events: SummaryStageEvent[];
  attempts: SummaryAttempt[];
  error_message: string | null;
  summary_public_id: string | null;
  triggered_by_public_id: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string | null;
}

interface WeekEntry {
  iso: string;
  start_date: string;
  end_date: string;
  has_summary: boolean;
}

// Gate 5 — pre-publish go/no-go ribbon. Mirrors PreflightResponse in
// backend/routers/admin_research.py verbatim; severity maps to ✓/⚠/✗.
interface PreflightCheck {
  label: string;
  severity: 'ok' | 'warn' | 'block';
  detail: string | null;
}

interface PreflightTopPick {
  insight_public_id: string;
  headline: string | null;
  has_infographic: boolean;
  infographic_provider: string | null;
  infographic_error: string | null;
  notebook_validator_status: string | null; // ok | ready | broken | pending | unknown
  notebook_validator_errors: string[];
}

interface PreflightResponse {
  week_iso: string;
  /** false if any check has severity='block'. */
  can_publish: boolean;
  checks: PreflightCheck[];
  bucket_counts: { a: number; b: number; c: number };
  inbox_count: number;
  inbox_aged_count: number;
  top_picks: PreflightTopPick[];
  /** 0 if no letter job ever ran for this week. */
  last_letter_attempts: number;
  /** Outcome of the LAST attempt of the most recent job:
   * success | intro_quality | json_parse | schema | invoke_error | null. */
  last_letter_outcome: string | null;
  source_run_summary: Record<string, {
    last_status: string;
    count: number | null;
    is_retry: boolean;
    duration_ms: number | null;
  }>;
}

function formatWeekRange(weekIso: string): string {
  if (!weekIso || !/^\d{4}-W\d{2}$/.test(weekIso)) return weekIso;
  const [yStr, wStr] = weekIso.split('-W');
  const year = parseInt(yStr, 10);
  const week = parseInt(wStr, 10);
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7;
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - (jan4Day - 1) + (week - 1) * 7);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  return `${fmt(monday)} – ${fmt(sunday).replace(/^[A-Za-z]+\s/, '')}, ${year}`;
}

function currentIsoWeek(): string {
  const now = new Date();
  const jan4 = new Date(Date.UTC(now.getUTCFullYear(), 0, 4));
  const diffDays = Math.floor((Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) - jan4.getTime()) / 86400000);
  const jan4Day = jan4.getUTCDay() || 7;
  const week = Math.floor((diffDays + (jan4Day - 1)) / 7) + 1;
  return `${now.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}

// Build a window of ISO week keys around `baseIso`, going `back` weeks
// backward and `forward` weeks forward (inclusive). Used to populate the
// week picker so an admin can always pick recent past weeks even if the
// backend has no insights for them yet.
function isoWeekWindow(baseIso: string, back: number, forward: number): string[] {
  const m = /^(\d{4})-W(\d{2})$/.exec(baseIso);
  if (!m) return [baseIso];
  const [yStr, wStr] = [m[1], m[2]];
  const year = parseInt(yStr, 10);
  const week = parseInt(wStr, 10);
  // Anchor on the Monday of baseIso, then walk 7-day steps both ways.
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7;
  const anchor = new Date(jan4);
  anchor.setUTCDate(jan4.getUTCDate() - (jan4Day - 1) + (week - 1) * 7);
  const out: string[] = [];
  for (let offset = -back; offset <= forward; offset++) {
    const d = new Date(anchor);
    d.setUTCDate(anchor.getUTCDate() + offset * 7);
    // Re-derive ISO year/week from the Monday date.
    const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    target.setUTCDate(target.getUTCDate() + 4 - (target.getUTCDay() || 7));
    const tYear = target.getUTCFullYear();
    const tJan1 = new Date(Date.UTC(tYear, 0, 1));
    const tWeek = Math.ceil(((target.getTime() - tJan1.getTime()) / 86400000 + 1) / 7);
    out.push(`${tYear}-W${String(tWeek).padStart(2, '0')}`);
  }
  return out;
}

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Returns true if `err` is a 401 (caller should stop). When true the
 * onUnauth callback fires — typical use is `navigate('/login')`. JWT
 * TTLs are bounded so admins occasionally land on this when their
 * session quietly expired between page loads.
 */
function isAuthError(err: unknown, onUnauth: () => void): boolean {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 401) {
    onUnauth();
    return true;
  }
  return false;
}

/**
 * Reusable per-tab guidance card. Every Research Editor tab gets one of
 * these at the top so a fresh admin can walk in cold and know:
 *   1. What this tab is for
 *   2. What to look at first
 *   3. What action to take next
 *
 * The card is visually quiet but always present — admins skim it the
 * first few times, then learn to ignore it. The `tone` prop colors the
 * left border so each tab feels distinct (not just yet another grey
 * card).
 */
function TabGuidance({
  title,
  purpose,
  steps,
  tone = 'indigo',
}: {
  title: string;
  /** One sentence answering "what does this tab do for me?". */
  purpose: string;
  /** 2–4 numbered steps the admin should follow on this tab. */
  steps: string[];
  tone?: 'indigo' | 'amber' | 'emerald' | 'violet';
}) {
  const accent: Record<string, string> = {
    indigo: 'border-l-indigo-500 bg-indigo-950/10',
    amber: 'border-l-amber-500 bg-amber-950/10',
    emerald: 'border-l-emerald-500 bg-emerald-950/10',
    violet: 'border-l-violet-500 bg-violet-950/10',
  };
  return (
    <div className={`mb-5 rounded-r-lg border border-neutral-800 border-l-4 ${accent[tone]} p-3.5`}>
      <div className="text-sm font-semibold text-neutral-200">{title}</div>
      <p className="mt-0.5 text-xs text-neutral-400">{purpose}</p>
      {steps.length > 0 && (
        <ol className="mt-2 space-y-0.5 text-xs text-neutral-300 list-decimal list-inside marker:text-neutral-500">
          {steps.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ol>
      )}
    </div>
  );
}

export default function AdminResearchPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabId>('pipeline-runs');
  const [weekIso, setWeekIso] = useState<string>(currentIsoWeek());
  const [weeks, setWeeks] = useState<WeekEntry[]>([]);

  // Intake state
  const [jobs, setJobs] = useState<JobResponse[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Drafts state
  const [insights, setInsights] = useState<InsightRow[]>([]);
  const [insightsTotal, setInsightsTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<'draft' | 'published' | 'all'>('draft');
  const [expandedPid, setExpandedPid] = useState<string | null>(null);
  const [selectedPids, setSelectedPids] = useState<Set<string>>(new Set());
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  // Optional filter: show only drafts whose `created_as_draft` audit entry
  // points at this source email. Labelled with the publication name.
  const [sourceFilter, setSourceFilter] = useState<{ publicId: string; label: string } | null>(null);

  // Pipeline-Runs state — lifted from PipelineRunsTab so it survives
  // tab switches. Without this, navigating to Drafts and back wipes
  // the active-run card and the email-preview violet card.
  const [plActiveJobPid, setPlActiveJobPid] = useState<string | null>(null);
  const [plActiveJob, setPlActiveJob] = useState<JobWithTimings | null>(null);
  const [plInsights, setPlInsights] = useState<InsightRow[]>([]);
  const [plSummaryPreview, setPlSummaryPreview] = useState<any | null>(null);

  // Weekly state
  const [readiness, setReadiness] = useState<ReadinessResponse | null>(null);
  const [weeklySummary, setWeeklySummary] = useState<any | null>(null);
  // Publishing + saving remain synchronous. Generation is async via job rows
  // and lives in `activeJob` below.
  const [weeklyBusy, setWeeklyBusy] = useState<'idle' | 'publishing' | 'saving'>('idle');
  const [weeklyError, setWeeklyError] = useState<string | null>(null);
  const [activeJob, setActiveJob] = useState<WeeklySummaryJob | null>(null);
  const [jobHistory, setJobHistory] = useState<WeeklySummaryJob[]>([]);
  // Gate 5 — go/no-go preflight ribbon. Fetched on demand only (tab
  // mount, week change, manual re-run, after generate/publish) — never
  // on an interval.
  const [preflight, setPreflight] = useState<PreflightResponse | null>(null);
  const [preflightLoading, setPreflightLoading] = useState(false);

  // ── Bootstrap: fetch weeks list + jobs ────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const meta = await axios.get(`${getApiUrl()}/api/v1/research-insights/meta`, { headers: authHeaders() });
        const w: WeekEntry[] = meta.data?.weeks || [];
        setWeeks(w);
        // Always land on the current ISO week. The /meta endpoint only
        // returns weeks with published content, so it CAN'T tell us where
        // "now" is — it'll often only return last week or older. Trust
        // the initial useState(currentIsoWeek()) and never override it
        // here. Empty current weeks just show "no insights" — admin
        // kicks off a Pipeline Run from there.
      } catch (_err) { /* feed may be empty */ }
    })();
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      const r = await axios.get(`${getApiUrl()}/api/v1/admin/research/jobs?limit=20`, { headers: authHeaders() });
      setJobs(r.data || []);
    } catch (_err) { /* noop */ }
  }, []);

  const dismissJob = useCallback(async (publicId: string) => {
    try {
      await axios.delete(`${getApiUrl()}/api/v1/admin/research/jobs/${publicId}`, { headers: authHeaders() });
      setJobs(prev => prev.filter(j => j.public_id !== publicId));
    } catch (err) {
      if (isAuthError(err, () => navigate('/login'))) return;
      const detail = (err as { response?: { data?: { detail?: string } }; message?: string })
        ?.response?.data?.detail
        || (err as { message?: string })?.message
        || 'Dismiss failed.';
      setUploadError(detail);
    }
  }, [navigate]);

  const cancelJob = useCallback(async (publicId: string) => {
    try {
      const r = await axios.post<JobResponse>(
        `${getApiUrl()}/api/v1/admin/research/jobs/${publicId}/cancel`,
        {},
        { headers: authHeaders() },
      );
      // Update the row in place so the user sees status='cancelled'
      // immediately, without waiting for the next poll tick.
      setJobs(prev => prev.map(j => (j.public_id === publicId ? r.data : j)));
    } catch (err) {
      if (isAuthError(err, () => navigate('/login'))) return;
      const detail = (err as { response?: { data?: { detail?: string } }; message?: string })
        ?.response?.data?.detail
        || (err as { message?: string })?.message
        || 'Cancel failed.';
      setUploadError(detail);
    }
  }, [navigate]);

  const clearFailedJobs = useCallback(async () => {
    const failed = jobs.filter(j => j.status === 'failed');
    if (failed.length === 0) return;
    if (!window.confirm(`Dismiss ${failed.length} failed job${failed.length === 1 ? '' : 's'}?`)) return;
    // Sequential to keep the spinner honest and surface the first error.
    for (const j of failed) {
      try {
        await axios.delete(`${getApiUrl()}/api/v1/admin/research/jobs/${j.public_id}`, { headers: authHeaders() });
      } catch (_e) { /* continue — partial cleanup is fine */ }
    }
    fetchJobs();
  }, [jobs, fetchJobs]);

  // Jump from a job's "View N drafts" link → Drafts tab, filtered to that
  // job's source email. We scope by source_email because a job only ever
  // has one source email, and sourceFilter already exists on Drafts.
  const jumpToDraftsForJob = useCallback((job: JobResponse) => {
    if (!job.source_email_public_id) return;
    setSourceFilter({
      publicId: job.source_email_public_id,
      label: job.source_email_filename || 'source email',
    });
    setStatusFilter('all');
    setTab('drafts');
  }, []);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // Poll active jobs every 2s.
  useEffect(() => {
    const anyActive = jobs.some(j => !['completed', 'failed'].includes(j.status));
    if (!anyActive) return;
    const interval = setInterval(fetchJobs, 2000);
    return () => clearInterval(interval);
  }, [jobs, fetchJobs]);

  // Noise drafts — title-only headlines with no abstract / no quant
  // keywords / no Bedrock enrichment. The button only renders when the
  // count is > 0; we re-poll the count whenever the Drafts tab loads
  // or the week changes (see the useEffect below).
  const [noisePreview, setNoisePreview] = useState<{ eligible: number; total_drafts: number } | null>(null);
  const fetchNoisePreview = useCallback(async () => {
    try {
      const r = await axios.get(
        `${getApiUrl()}/api/v1/admin/research/insights/noise-preview`,
        { params: { week_iso: weekIso }, headers: authHeaders() },
      );
      setNoisePreview({ eligible: r.data.eligible, total_drafts: r.data.total_drafts });
    } catch (err) {
      if (isAuthError(err, () => navigate('/login'))) return;
      // Non-fatal — just hide the button.
      setNoisePreview(null);
    }
  }, [weekIso, navigate]);

  const fetchInsights = useCallback(async () => {
    const params: Record<string, string> = {
      week_iso: weekIso,
      limit: String(pageSize),
      offset: String(offset),
    };
    if (statusFilter !== 'all') params.status = statusFilter;
    if (sourceFilter) params.source_email_public_id = sourceFilter.publicId;
    const r = await axios.get(`${getApiUrl()}/api/v1/admin/research/insights`, { params, headers: authHeaders() });
    setInsights(r.data?.insights || []);
    setInsightsTotal(r.data?.total || 0);
    setSelectedPids(new Set());
  }, [weekIso, statusFilter, pageSize, offset, sourceFilter]);

  // Reset pagination whenever the slice being viewed changes. Avoids
  // landing on an empty page after a filter narrows the result set.
  useEffect(() => { setOffset(0); }, [weekIso, statusFilter, pageSize, sourceFilter]);

  const fetchReadiness = useCallback(async () => {
    try {
      const r = await axios.get(`${getApiUrl()}/api/v1/admin/research/weekly-summary/${weekIso}/readiness`, { headers: authHeaders() });
      setReadiness(r.data);
    } catch (_err) { setReadiness(null); }
  }, [weekIso]);

  const fetchPreflight = useCallback(async () => {
    setPreflightLoading(true);
    try {
      const r = await axios.get(
        `${getApiUrl()}/api/v1/admin/research/weekly-summary/${weekIso}/preflight`,
        { headers: authHeaders() },
      );
      setPreflight(r.data);
    } catch (err) {
      if (isAuthError(err, () => navigate('/login'))) return;
      setPreflight(null);
    } finally {
      setPreflightLoading(false);
    }
  }, [weekIso, navigate]);

  const fetchWeeklySummary = useCallback(async () => {
    try {
      const r = await axios.get(`${getApiUrl()}/api/v1/research-insights/admin/weekly-summary/${weekIso}`, { headers: authHeaders() });
      setWeeklySummary(r.data);
    } catch (_err) { setWeeklySummary(null); }
  }, [weekIso]);

  const fetchJobHistory = useCallback(async () => {
    try {
      const r = await axios.get(
        `${getApiUrl()}/api/v1/research-insights/admin/weekly-summary/${weekIso}/jobs`,
        { headers: authHeaders() },
      );
      const rows: WeeklySummaryJob[] = r.data || [];
      setJobHistory(rows);
      // Adopt an active job if one already exists (admin reloaded the
      // page mid-run). Don't clobber a more recent `activeJob` from
      // local poll — keep the one we already have if it matches.
      const active = rows.find(j => j.status === 'pending' || j.status === 'running');
      if (active) {
        setActiveJob(prev => prev && prev.public_id === active.public_id ? prev : active);
      }
    } catch (_err) { /* noop */ }
  }, [weekIso]);

  useEffect(() => {
    if (tab === 'drafts') {
      fetchInsights();
      fetchNoisePreview();
    }
    if (tab === 'weekly') { fetchReadiness(); fetchWeeklySummary(); fetchJobHistory(); fetchPreflight(); }
  }, [tab, fetchInsights, fetchReadiness, fetchWeeklySummary, fetchJobHistory, fetchNoisePreview, fetchPreflight]);

  // Poll active weekly-summary job every 2s until it reaches a terminal
  // state. Reuses the same cadence as the Intake jobs list so the UI
  // feels consistent.
  useEffect(() => {
    if (!activeJob) return;
    if (activeJob.status !== 'pending' && activeJob.status !== 'running') return;
    const tick = async () => {
      try {
        const r = await axios.get(
          `${getApiUrl()}/api/v1/research-insights/admin/weekly-summary/jobs/${activeJob.public_id}`,
          { headers: authHeaders() },
        );
        const next: WeeklySummaryJob = r.data;
        setActiveJob(next);
        if (next.status === 'completed' || next.status === 'failed' || next.status === 'cancelled') {
          // Terminal — grab the freshly-persisted summary + refresh
          // history and the preflight ribbon so the verdict reflects
          // the run that just finished.
          fetchWeeklySummary();
          fetchJobHistory();
          fetchPreflight();
        }
      } catch (_err) { /* noop, next tick retries */ }
    };
    const interval = setInterval(tick, 2000);
    return () => clearInterval(interval);
  }, [activeJob, fetchWeeklySummary, fetchJobHistory, fetchPreflight]);

  // ── Email upload ─────────────────────────────────────────────────

  const uploadFiles = useCallback(async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    setUploadError(null);
    try {
      const form = new FormData();
      for (let i = 0; i < fileList.length; i++) form.append('files', fileList[i]);
      await axios.post(`${getApiUrl()}/api/v1/admin/research/emails`, form, { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } });
      fetchJobs();
    } catch (err) {
      // Token expired — bounce to /login. Other admin pages do the
      // same; the user comes back here authenticated.
      if (isAuthError(err, () => navigate('/login'))) return;
      const detail = (err as { response?: { data?: { detail?: string } }; message?: string })
        ?.response?.data?.detail
        || (err as { message?: string })?.message
        || 'Upload failed.';
      setUploadError(detail);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [fetchJobs, navigate]);

  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    uploadFiles(e.dataTransfer.files);
  }, [uploadFiles]);

  // ── Drafts: inline edit ──────────────────────────────────────────

  const savePatch = useCallback(async (publicId: string, patch: Partial<InsightRow>) => {
    const payload: Record<string, unknown> = {};
    if (patch.paper_title !== undefined) payload.paper_title = patch.paper_title;
    if (patch.summary_md !== undefined) payload.summary_md = patch.summary_md;
    if (patch.classification !== undefined) payload.classification = patch.classification;
    if (patch.propalgos_relevance !== undefined) payload.propalgos_relevance = patch.propalgos_relevance;
    if (patch.corroborated !== undefined) payload.corroborated = patch.corroborated;
    if (patch.recommended_gpu_tier !== undefined) payload.recommended_gpu_tier = patch.recommended_gpu_tier;
    if (patch.feature_tags !== undefined) payload.feature_tags = patch.feature_tags;
    if (patch.actionability_json !== undefined) payload.actionability_json = patch.actionability_json;
    const r = await axios.patch(`${getApiUrl()}/api/v1/admin/research/insights/${publicId}`, payload, { headers: authHeaders() });
    setInsights(prev => prev.map(i => i.public_id === publicId ? r.data : i));
  }, []);

  const flipStatus = useCallback(async (publicId: string, toPublished: boolean) => {
    const endpoint = toPublished ? 'publish' : 'unpublish';
    const r = await axios.post(`${getApiUrl()}/api/v1/admin/research/insights/${publicId}/${endpoint}`, {}, { headers: authHeaders() });
    setInsights(prev => prev.map(i => i.public_id === publicId ? r.data : i));
    fetchReadiness();
  }, [fetchReadiness]);

  const bulkDelete = useCallback(async () => {
    if (selectedPids.size === 0) return;
    const count = selectedPids.size;
    const reason = window.prompt(
      `Hard-delete ${count} draft${count === 1 ? '' : 's'}? This cannot be undone.\n\nOptional reason (logged to audit):`,
      '',
    );
    if (reason === null) return;  // cancelled
    await axios.post(
      `${getApiUrl()}/api/v1/admin/research/insights/bulk-action`,
      { action: 'delete', public_ids: Array.from(selectedPids), reason: reason || undefined },
      { headers: authHeaders() },
    );
    setSelectedPids(new Set());
    fetchInsights();
    fetchReadiness();
  }, [selectedPids, fetchInsights, fetchReadiness]);

  const deleteOne = useCallback(async (publicId: string, title: string) => {
    if (!window.confirm(`Hard-delete "${title.slice(0, 80)}"? This cannot be undone.`)) return;
    try {
      await axios.delete(
        `${getApiUrl()}/api/v1/admin/research/insights/${publicId}`,
        { headers: authHeaders() },
      );
      fetchInsights();
      fetchReadiness();
    } catch (err: any) {
      alert(err?.response?.data?.detail ?? 'Delete failed');
    }
  }, [fetchInsights, fetchReadiness]);

  const bulkPublish = useCallback(async () => {
    if (selectedPids.size === 0) return;
    await axios.post(`${getApiUrl()}/api/v1/admin/research/insights/bulk-publish`, { public_ids: Array.from(selectedPids) }, { headers: authHeaders() });
    await fetchInsights();
    fetchReadiness();
  }, [selectedPids, fetchInsights, fetchReadiness]);

  const pruneNoise = useCallback(async () => {
    const count = noisePreview?.eligible ?? 0;
    if (count === 0) return;
    if (!window.confirm(
      `Delete ${count} noise draft${count === 1 ? '' : 's'} for ${weekIso}?\n\n`
      + `These are title-only entries with no abstract, no quant keywords, and no Bedrock enrichment — nothing to review or publish.`,
    )) return;
    try {
      await axios.post(
        `${getApiUrl()}/api/v1/admin/research/insights/prune-noise`,
        null,
        { params: { week_iso: weekIso }, headers: authHeaders() },
      );
      await Promise.all([fetchInsights(), fetchNoisePreview()]);
      fetchReadiness();
    } catch (err) {
      if (isAuthError(err, () => navigate('/login'))) return;
      const detail = (err as { response?: { data?: { detail?: string } }; message?: string })
        ?.response?.data?.detail
        || (err as { message?: string })?.message
        || 'Prune failed.';
      setUploadError(detail);
    }
  }, [noisePreview, weekIso, fetchInsights, fetchReadiness, fetchNoisePreview, navigate]);

  const regenerate = useCallback(async (publicId: string) => {
    const r = await axios.post(`${getApiUrl()}/api/v1/admin/research/insights/${publicId}/regenerate-actionability`, {}, { headers: authHeaders() });
    setInsights(prev => prev.map(i => i.public_id === publicId ? r.data : i));
  }, []);

  // v3 — admin overrides on curator-routed rows.
  const undoAutoPublish = useCallback(async (publicId: string) => {
    try {
      const r = await axios.post(
        `${getApiUrl()}/api/v1/admin/research/insights/${publicId}/undo-auto-publish`,
        {},
        { headers: authHeaders() },
      );
      setInsights(prev => prev.map(i => i.public_id === publicId ? r.data : i));
      fetchReadiness();
    } catch (err) {
      if (isAuthError(err, () => navigate('/login'))) return;
      throw err;
    }
  }, [fetchReadiness, navigate]);

  const clearFlag = useCallback(async (publicId: string) => {
    try {
      const r = await axios.post(
        `${getApiUrl()}/api/v1/admin/research/insights/${publicId}/clear-flag`,
        {},
        { headers: authHeaders() },
      );
      setInsights(prev => prev.map(i => i.public_id === publicId ? r.data : i));
    } catch (err) {
      if (isAuthError(err, () => navigate('/login'))) return;
      throw err;
    }
  }, [navigate]);

  // ── Weekly summary ───────────────────────────────────────────────

  const generateSummary = useCallback(async () => {
    if (readiness && readiness.draft_count > 0 && readiness.recommendation) {
      const go = window.confirm(`${readiness.recommendation}\n\nGenerate anyway?`);
      if (!go) return;
    }
    setWeeklyError(null);
    try {
      const r = await axios.post(
        `${getApiUrl()}/api/v1/research-insights/admin/weekly-summary/${weekIso}/generate`,
        {},
        { headers: authHeaders() },
      );
      setActiveJob(r.data);
      fetchJobHistory();
      fetchPreflight();
    } catch (err: any) {
      setWeeklyError(err?.response?.data?.detail || err.message);
    }
  }, [weekIso, readiness, fetchJobHistory, fetchPreflight]);

  const dismissSummaryJob = useCallback(async (publicId: string) => {
    try {
      await axios.delete(
        `${getApiUrl()}/api/v1/research-insights/admin/weekly-summary/jobs/${publicId}`,
        { headers: authHeaders() },
      );
      setJobHistory(prev => prev.filter(j => j.public_id !== publicId));
      setActiveJob(prev => (prev && prev.public_id === publicId ? null : prev));
    } catch (err: any) {
      alert(`Dismiss failed: ${err?.response?.data?.detail || err.message}`);
    }
  }, []);

  const cancelSummaryJob = useCallback(async (publicId: string) => {
    // "Cancel" flips status → cancelled on the server. The local poll
    // picks the transition up on its next tick and moves the job into
    // the terminal-state rendering path (which shows the dismiss ×).
    try {
      const r = await axios.post(
        `${getApiUrl()}/api/v1/research-insights/admin/weekly-summary/jobs/${publicId}/cancel`,
        {},
        { headers: authHeaders() },
      );
      setActiveJob(prev => (prev && prev.public_id === publicId ? r.data : prev));
      setJobHistory(prev => prev.map(j => j.public_id === publicId ? r.data : j));
    } catch (err: any) {
      alert(`Cancel failed: ${err?.response?.data?.detail || err.message}`);
    }
  }, []);

  const publishSummary = useCallback(async () => {
    setWeeklyBusy('publishing');
    try {
      await axios.post(`${getApiUrl()}/api/v1/research-insights/admin/weekly-summary/${weekIso}/publish`, {}, { headers: authHeaders() });
      await fetchWeeklySummary();
      fetchPreflight();
    } finally {
      setWeeklyBusy('idle');
    }
  }, [weekIso, fetchWeeklySummary, fetchPreflight]);

  // Gate 5 escape hatch — publish the last intro-quality-failed attempt
  // as-is (backend pulls the attempt's parsed_payload; no Bedrock spend).
  // Only reachable from the UI when preflight reports the last letter
  // run burned its attempts on `intro_quality`.
  const forcePublishSummary = useCallback(async (stripLinks: boolean) => {
    setWeeklyBusy('publishing');
    setWeeklyError(null);
    try {
      await axios.post(
        `${getApiUrl()}/api/v1/admin/research/weekly-summary/${weekIso}/force-publish`,
        { strip_news_hook_links: stripLinks },
        { headers: authHeaders() },
      );
      await fetchWeeklySummary();
      fetchPreflight();
      fetchJobHistory();
    } catch (err: any) {
      if (isAuthError(err, () => navigate('/login'))) return;
      setWeeklyError(err?.response?.data?.detail || err.message);
    } finally {
      setWeeklyBusy('idle');
    }
  }, [weekIso, fetchWeeklySummary, fetchPreflight, fetchJobHistory, navigate]);

  const saveSummary = useCallback(async () => {
    if (!weeklySummary?.content) return;
    setWeeklyBusy('saving');
    try {
      const r = await axios.put(`${getApiUrl()}/api/v1/research-insights/admin/weekly-summary/${weekIso}`, { content: weeklySummary.content }, { headers: authHeaders() });
      setWeeklySummary(r.data);
    } finally {
      setWeeklyBusy('idle');
    }
  }, [weekIso, weeklySummary]);

  // ── Render ───────────────────────────────────────────────────────

  const draftCountBadge = readiness?.draft_count ?? 0;

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Research Editor</h1>
            <p className="text-sm text-neutral-500">Email intake, draft review, weekly letter editor.</p>
          </div>
          <div className="flex items-center gap-3">
            <label className="text-sm text-neutral-400">Week:</label>
            <select
              value={weekIso}
              onChange={(e) => setWeekIso(e.target.value)}
              className="rounded-md border border-neutral-800 bg-neutral-900 px-2 py-1 text-sm"
            >
              {(() => {
                // Always offer the last 10 weeks + current + next 2, even
                // on a fresh DB with no insights yet. Merge any weeks the
                // backend knows about (may include much older ones with
                // published summaries), dedupe, sort newest-first.
                const window = isoWeekWindow(currentIsoWeek(), 10, 2);
                const fromMeta = weeks.map(w => w.iso);
                const all = Array.from(new Set([weekIso, ...window, ...fromMeta]));
                all.sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
                return all.map(iso => (
                  <option key={iso} value={iso}>{formatWeekRange(iso)}</option>
                ));
              })()}
            </select>
          </div>
        </div>

        <div className="flex gap-1 border-b border-neutral-800 mb-6">
          {([
            { id: 'pipeline-runs', label: 'Pipeline Runs' },
            { id: 'drafts', label: `Drafts${draftCountBadge ? ` (${draftCountBadge})` : ''}` },
            { id: 'weekly', label: 'Weekly summary' },
            { id: 'top-picks', label: 'Top Picks' },
          ] as Array<{ id: TabId; label: string }>).map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t.id ? 'border-indigo-500 text-white' : 'border-transparent text-neutral-500 hover:text-neutral-300'}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'drafts' && (
          <DraftsTab
            weekIso={weekIso}
            insights={insights}
            total={insightsTotal}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            expandedPid={expandedPid}
            setExpandedPid={setExpandedPid}
            selectedPids={selectedPids}
            setSelectedPids={setSelectedPids}
            offset={offset}
            setOffset={setOffset}
            pageSize={pageSize}
            setPageSize={setPageSize}
            sourceFilter={sourceFilter}
            setSourceFilter={setSourceFilter}
            savePatch={savePatch}
            flipStatus={flipStatus}
            bulkPublish={bulkPublish}
            bulkDelete={bulkDelete}
            deleteOne={deleteOne}
            noiseEligible={noisePreview?.eligible ?? 0}
            onPruneNoise={pruneNoise}
            regenerate={regenerate}
            refetch={fetchInsights}
            onUndoAutoPublish={undoAutoPublish}
            onClearFlag={clearFlag}
          />
        )}

        {tab === 'weekly' && (
          <WeeklyTab
            weekIso={weekIso}
            readiness={readiness}
            preflight={preflight}
            preflightLoading={preflightLoading}
            onRefreshPreflight={fetchPreflight}
            onForcePublish={forcePublishSummary}
            summary={weeklySummary}
            setSummary={setWeeklySummary}
            busy={weeklyBusy}
            error={weeklyError}
            activeJob={activeJob}
            jobHistory={jobHistory}
            onGenerate={generateSummary}
            onSave={saveSummary}
            onPublish={publishSummary}
            onJumpToDrafts={() => setTab('drafts')}
            onDismissJob={dismissSummaryJob}
            onCancelJob={cancelSummaryJob}
          />
        )}

        {tab === 'top-picks' && (
          <TopPicksTab
            weekIso={weekIso}
            summary={weeklySummary}
            setSummary={setWeeklySummary}
            onSaveSummary={saveSummary}
          />
        )}

        {tab === 'pipeline-runs' && (
          <PipelineRunsTab
            weekIso={weekIso}
            onJumpToDrafts={(testWeekIso: string) => {
              setWeekIso(testWeekIso);
              setTab('drafts');
              setStatusFilter('all');
            }}
            jobs={jobs}
            uploading={uploading}
            uploadError={uploadError}
            dragActive={dragActive}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
            onFilePick={() => fileInputRef.current?.click()}
            fileInputRef={fileInputRef}
            onFileChange={(e) => uploadFiles(e.target.files)}
            onDismissJob={dismissJob}
            onCancelJob={cancelJob}
            onClearFailed={clearFailedJobs}
            onJumpToDrafts2={jumpToDraftsForJob}
            onIngestionStarted={fetchJobs}
            activeJobPid={plActiveJobPid}
            setActiveJobPid={setPlActiveJobPid}
            activeJob={plActiveJob}
            setActiveJob={setPlActiveJob}
            insights={plInsights}
            setInsights={setPlInsights}
            summaryPreview={plSummaryPreview}
            setSummaryPreview={setPlSummaryPreview}
          />
        )}
      </div>
    </div>
  );
}

// ─── Discover-week panel ────────────────────────────────────────────

interface DiscoverResponse {
  run_public_id: string;
  week_iso: string;
  sources: string[];
  generate_specs: boolean;
  status: string;
}

function DiscoverWeekPanel({ weekIso }: { weekIso: string }) {
  const navigate = useNavigate();
  const [arxiv, setArxiv] = useState(true);
  const [ssrn, setSsrn] = useState(true);
  const [nber, setNber] = useState(true);
  const [generateSpecs, setGenerateSpecs] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState<DiscoverResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = useCallback(async () => {
    const sources = [
      arxiv ? 'arxiv' : null,
      ssrn ? 'ssrn' : null,
      nber ? 'nber' : null,
    ].filter((s): s is string => !!s);
    if (sources.length === 0) {
      setError('Pick at least one source.');
      return;
    }
    if (!/^\d{4}-W\d{2}$/.test(weekIso)) {
      setError('Selected week is invalid — pick one from the top-right.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const resp = await axios.post<DiscoverResponse>(
        `${getApiUrl()}/api/v1/admin/research/discover-week`,
        { week_iso: weekIso, sources, generate_specs: generateSpecs },
        { headers: { ...authHeaders() } },
      );
      setLastResult(resp.data);
    } catch (err) {
      if (isAuthError(err, () => navigate('/login'))) return;
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || 'Discovery request failed. See server logs.');
    } finally {
      setSubmitting(false);
    }
  }, [weekIso, arxiv, ssrn, nber, generateSpecs, navigate]);

  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-950 p-5 space-y-4">
      <div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-neutral-200">Run weekly discovery</span>
          <span className="text-xs px-2 py-0.5 rounded bg-emerald-900/40 text-emerald-300 border border-emerald-800/60">new</span>
          <span className="ml-auto text-xs text-neutral-500">
            Target: <span className="text-neutral-300">{formatWeekRange(weekIso)}</span>
            <span className="text-neutral-600 ml-1">(change in top-right)</span>
          </span>
        </div>
        <p className="text-xs text-neutral-500 mt-1">
          Pull fresh papers from arXiv + SSRN + NBER for the selected ISO week.
          Enrichment + Bedrock are capped at the top 60 papers by relevance (high/medium tier) so
          a weekly run finishes in ~10 min, not an hour. The remaining low-signal papers persist
          to the catalog but skip enrichment. Safe to run alongside a manual newsletter upload —
          the second arrival merges into existing drafts (marked <span className="text-neutral-400">corroborated</span>) rather than duplicating.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <fieldset className="flex flex-col gap-1">
          <span className="text-xs text-neutral-500">Sources</span>
          <div className="flex items-center gap-3 text-sm">
            <label className="flex items-center gap-1.5 text-neutral-300">
              <input type="checkbox" checked={arxiv} onChange={(e) => setArxiv(e.target.checked)} className="accent-indigo-500" />
              arXiv
            </label>
            <label className="flex items-center gap-1.5 text-neutral-300">
              <input type="checkbox" checked={ssrn} onChange={(e) => setSsrn(e.target.checked)} className="accent-indigo-500" />
              SSRN
            </label>
            <label className="flex items-center gap-1.5 text-neutral-300">
              <input type="checkbox" checked={nber} onChange={(e) => setNber(e.target.checked)} className="accent-indigo-500" />
              NBER
            </label>
          </div>
        </fieldset>

        <label className="flex items-center gap-2 text-sm text-neutral-300 self-end mb-1">
          <input
            type="checkbox"
            checked={generateSpecs}
            onChange={(e) => setGenerateSpecs(e.target.checked)}
            className="accent-indigo-500"
          />
          Generate notebook specs (Bedrock)
        </label>

        <button
          onClick={onSubmit}
          disabled={submitting}
          className="ml-auto rounded-md bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-sm font-medium text-white"
        >
          {submitting ? 'Starting…' : 'Run discovery'}
        </button>
      </div>

      {error && (
        <div className="text-xs text-red-300 bg-red-900/30 border border-red-800/50 rounded px-3 py-2">
          {error}
        </div>
      )}

      {lastResult && (
        <div className="text-xs text-neutral-400 bg-neutral-900 border border-neutral-800 rounded px-3 py-2">
          Discovery <span className="font-mono text-emerald-300">{lastResult.status}</span> for{' '}
          <span className="font-mono text-neutral-300">{formatWeekRange(lastResult.week_iso)}</span> via{' '}
          <span className="text-neutral-300">{lastResult.sources.join(', ')}</span>.
          Run id <span className="font-mono text-neutral-500">{lastResult.run_public_id.slice(0, 8)}</span>.
          {' '}Drafts will appear in the Drafts tab as the run finishes (a few minutes for arXiv-only;
          longer for SSRN/NBER batches). Server logs hold the live status.
        </div>
      )}
    </div>
  );
}

function formatAbsoluteTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function formatRelativeTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

function formatDuration(startIso: string | null, endIso: string | null): string {
  if (!startIso || !endIso) return '';
  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();
  if (isNaN(start) || isNaN(end) || end < start) return '';
  const secs = Math.floor((end - start) / 1000);
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
}

function JobRow({ job, onDismiss, onCancel, onJumpToDrafts }: {
  job: JobResponse;
  onDismiss: () => void;
  onCancel: () => Promise<void>;
  onJumpToDrafts: () => void;
}) {
  const [errorOpen, setErrorOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const isCompleted = job.status === 'completed';
  const isFailed = job.status === 'failed';
  const isCancelled = job.status === 'cancelled';
  const isActive = !isCompleted && !isFailed && !isCancelled;
  const color = isCompleted ? 'bg-emerald-500' : isFailed ? 'bg-red-500' : isCancelled ? 'bg-neutral-600' : 'bg-indigo-500';
  const draftCount = job.draft_insight_public_ids.length;

  // Pick a timestamp label based on state — active jobs show "started", done
  // jobs show "finished", nothing-started-yet shows "queued".
  const timeLabel = (() => {
    if (isActive && job.started_at) return `started ${formatRelativeTime(job.started_at)}`;
    if (!isActive && job.completed_at) return `finished ${formatRelativeTime(job.completed_at)}`;
    if (job.created_at) return `queued ${formatRelativeTime(job.created_at)}`;
    return '';
  })();
  const duration = formatDuration(job.started_at, job.completed_at);
  const hasError = isFailed && !!job.error_message;

  const isDiscovery = job.kind === 'discovery';
  const displayLabel = isDiscovery
    ? `Discovery: ${(job.sources || []).join(' + ') || '?'}${job.week_iso ? ` · ${formatWeekRange(job.week_iso)}` : ''}`
    : (job.source_email_filename || '(no filename)');

  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-900/40 p-3">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {isDiscovery && (
              <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-emerald-500/15 text-emerald-300">
                discovery
              </span>
            )}
            <span className="text-sm font-medium truncate">{displayLabel}</span>
            {isCompleted && (
              <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-emerald-500/15 text-emerald-300">completed</span>
            )}
            {isFailed && (
              <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-red-500/15 text-red-300">failed</span>
            )}
            {isCancelled && (
              <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-neutral-700/40 text-neutral-300">cancelled</span>
            )}
            {isActive && (
              <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-indigo-500/15 text-indigo-300">
                {job.current_step || job.status}
              </span>
            )}
          </div>
          <div className="text-xs text-neutral-500 mt-1 flex items-center gap-2 flex-wrap">
            <span>{job.papers_enriched}/{job.papers_total} enriched</span>
            {draftCount > 0 && (
              <>
                <span className="text-neutral-700">·</span>
                <button
                  onClick={onJumpToDrafts}
                  className="text-indigo-300 hover:text-indigo-200 underline-offset-2 hover:underline"
                  title="Open Drafts tab filtered to this email's papers."
                >
                  {draftCount} draft{draftCount === 1 ? '' : 's'} →
                </button>
              </>
            )}
            {duration && (
              <>
                <span className="text-neutral-700">·</span>
                <span title="How long the job took from start to finish.">took {duration}</span>
              </>
            )}
            {timeLabel && (
              <>
                <span className="text-neutral-700">·</span>
                <span
                  title={
                    isActive
                      ? `Started ${formatAbsoluteTime(job.started_at)}`
                      : isCompleted
                        ? `Finished ${formatAbsoluteTime(job.completed_at)}`
                        : `Queued ${formatAbsoluteTime(job.created_at)}`
                  }
                >
                  {timeLabel}
                </span>
              </>
            )}
          </div>
        </div>
        {isActive && (
          <button
            onClick={async () => {
              setCancelling(true);
              try { await onCancel(); } finally { setCancelling(false); }
            }}
            disabled={cancelling}
            className="text-xs px-2 py-1 rounded border border-neutral-700 text-neutral-400 hover:text-red-300 hover:border-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Cancel this job. The pipeline checks at every stage, so cancellation lands at the next stage boundary — usually within a few seconds."
            aria-label="Cancel job"
          >
            {cancelling ? 'Cancelling…' : 'Cancel'}
          </button>
        )}
        {!isActive && (
          <button
            onClick={onDismiss}
            className="text-neutral-600 hover:text-red-300 text-lg leading-none px-1"
            title="Dismiss this job from the list. Drafts it created remain intact."
            aria-label="Dismiss job"
          >
            ×
          </button>
        )}
      </div>

      <div className="h-1.5 mt-2 rounded-full bg-neutral-800 overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${job.progress_pct}%` }} />
      </div>

      {hasError && (
        <div className="mt-2">
          <button
            onClick={() => setErrorOpen(o => !o)}
            className="text-[11px] text-red-300 hover:text-red-200"
          >
            {errorOpen ? '▾ Hide error' : '▸ Show error'}
          </button>
          {errorOpen && (
            <pre className="mt-1 rounded border border-red-500/20 bg-red-500/5 p-2 text-[11px] text-red-200 whitespace-pre-wrap break-words">
              {job.error_message}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Drafts tab ─────────────────────────────────────────────────────

function DraftsTab(props: {
  weekIso: string;
  insights: InsightRow[];
  total: number;
  statusFilter: 'draft' | 'published' | 'all';
  setStatusFilter: (v: 'draft' | 'published' | 'all') => void;
  expandedPid: string | null;
  setExpandedPid: (v: string | null) => void;
  selectedPids: Set<string>;
  setSelectedPids: (v: Set<string>) => void;
  offset: number;
  setOffset: (v: number) => void;
  pageSize: number;
  setPageSize: (v: number) => void;
  sourceFilter: { publicId: string; label: string } | null;
  setSourceFilter: (v: { publicId: string; label: string } | null) => void;
  savePatch: (pid: string, patch: Partial<InsightRow>) => Promise<void>;
  flipStatus: (pid: string, toPublished: boolean) => Promise<void>;
  bulkPublish: () => Promise<void>;
  bulkDelete: () => Promise<void>;
  deleteOne: (pid: string, title: string) => Promise<void>;
  noiseEligible: number;
  onPruneNoise: () => Promise<void>;
  regenerate: (pid: string) => Promise<void>;
  refetch: () => Promise<void>;
  // v3 — optional admin override + spot-check actions per row.
  onUndoAutoPublish?: (pid: string) => Promise<void>;
  onClearFlag?: (pid: string) => Promise<void>;
}) {
  const toggleSelect = (pid: string) => {
    const next = new Set(props.selectedPids);
    if (next.has(pid)) next.delete(pid); else next.add(pid);
    props.setSelectedPids(next);
  };
  const selectAll = () => props.setSelectedPids(new Set(props.insights.map(i => i.public_id)));
  const clearSel = () => props.setSelectedPids(new Set());

  const start = props.insights.length === 0 ? 0 : props.offset + 1;
  const end = props.offset + props.insights.length;
  const hasPrev = props.offset > 0;
  const hasNext = props.offset + props.insights.length < props.total;

  const isTestWeek = props.weekIso.includes('-TEST-');
  return (
    <div>
      <TabGuidance
        title="Drafts — review what the curator decided"
        purpose={
          isTestWeek
            ? 'Per-paper review of a TEST run\'s output. Sanity-check the bucket distribution + individual rows before deciding whether to promote to prod.'
            : 'Per-paper review of this week\'s curated insights. Anything in "draft" status is awaiting your decision; anything "published" is already on the Research Feed.'
        }
        steps={[
          'Use the Curator activity card to see the bucket split. Click "Triage N →" if there are inbox rows that need a decision.',
          'For each draft row: skim title + verdict + suggested edits. Click Apply on suggestions you like, then Publish (or Reject for noise).',
          'Bulk operations: multi-select via row checkboxes, then "Publish selected" or "Delete selected" to move quickly.',
          isTestWeek
            ? 'When done, head back to Pipeline Runs and Promote to prod (or cancel and re-tune the test).'
            : 'Once drafts are zero, jump to the Weekly summary tab to generate the editorial letter.',
        ]}
        tone={isTestWeek ? 'amber' : 'indigo'}
      />

      {isTestWeek && (
        <div className="mb-4 rounded-lg border border-amber-700 bg-amber-900/20 p-3 text-sm text-amber-200 flex items-center gap-3">
          <span className="px-2 py-0.5 bg-amber-900/60 border border-amber-700 rounded text-[10px] uppercase tracking-wide font-mono font-semibold">TEST DATA</span>
          <span className="flex-1">
            Viewing test-run drafts for week <span className="font-mono">{props.weekIso}</span>.
            These rows are isolated from prod queries by week and will be cleaned up after 24h
            (or via the cleanup button on the Pipeline Runs tab).
          </span>
        </div>
      )}
      {/* v3 — Curator activity at a glance. Only fetches once per week
          + tab switch so it's safe to mount unconditionally. */}
      <CurationSummaryCard
        weekIso={props.weekIso}
        onJumpToInbox={() => {
          // Reset every filter that could be hiding inbox rows, then
          // scroll the row list into view so the click feels responsive
          // even when the filter was already 'draft' (the default).
          props.setStatusFilter('draft');
          props.setSourceFilter(null);
          props.setOffset(0);
          props.setSelectedPids(new Set());
          // Use a microtask delay so the re-render lands first.
          setTimeout(() => {
            document
              .getElementById('drafts-row-list')
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 0);
        }}
      />

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex rounded-md border border-neutral-800 overflow-hidden">
          {(['draft', 'published', 'all'] as const).map(s => (
            <button
              key={s}
              onClick={() => props.setStatusFilter(s)}
              className={`px-3 py-1.5 text-xs ${props.statusFilter === s ? 'bg-neutral-800 text-white' : 'text-neutral-400 hover:text-white'}`}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="text-xs text-neutral-500">
          {props.total === 0 ? 'No rows' : <>Rows <span className="text-neutral-300">{start}–{end}</span> of {props.total}</>}
          {' · '}week {formatWeekRange(props.weekIso)}
        </div>
        {props.sourceFilter && (
          <div className="flex items-center gap-1.5 rounded-full border border-indigo-500/30 bg-indigo-500/10 px-2.5 py-1 text-[11px] text-indigo-200">
            <span className="text-indigo-400">from:</span>
            <span className="truncate max-w-[240px]">{props.sourceFilter.label}</span>
            <button
              onClick={() => props.setSourceFilter(null)}
              className="ml-1 text-indigo-300 hover:text-white"
              title="Clear source filter"
            >
              ×
            </button>
          </div>
        )}
        <div className="ml-auto flex gap-2 items-center">
          {props.selectedPids.size > 0 && (
            <>
              <button
                onClick={props.bulkPublish}
                className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-500"
              >
                Publish selected ({props.selectedPids.size})
              </button>
              <button
                onClick={props.bulkDelete}
                className="rounded-md bg-red-900/50 border border-red-700 px-3 py-1.5 text-xs font-medium text-red-200 hover:bg-red-900/70"
                title="Hard-delete selected rows. Cannot be undone."
              >
                Delete ({props.selectedPids.size})
              </button>
              <button onClick={clearSel} className="text-xs text-neutral-500 hover:text-neutral-300">clear</button>
            </>
          )}
          {props.selectedPids.size === 0 && props.insights.length > 0 && (
            <button onClick={selectAll} className="text-xs text-neutral-500 hover:text-neutral-300">select all</button>
          )}
          {props.noiseEligible > 0 && (
            <button
              onClick={props.onPruneNoise}
              className="rounded-md border border-amber-700/50 bg-amber-900/20 px-2.5 py-1 text-xs text-amber-200 hover:border-amber-600 hover:bg-amber-900/40"
              title="Delete title-only drafts that have no abstract, no quant keyword match, and no Bedrock enrichment. Won't touch published or admin-edited rows."
            >
              Prune {props.noiseEligible} noise draft{props.noiseEligible === 1 ? '' : 's'}
            </button>
          )}
          <button onClick={props.refetch} className="text-xs text-neutral-500 hover:text-neutral-300">refresh</button>
        </div>
      </div>

      {props.insights.length > 0 && (
        <div
          className="flex items-center gap-3 px-3 py-1.5 text-[10px] uppercase tracking-wider text-neutral-500 border-b border-neutral-900"
        >
          <span className="w-4" />
          <span className="flex-1">Paper</span>
          <span
            className="w-40 text-right"
            title="The ingested email + issue this draft was extracted from."
          >
            Source
          </span>
          <span
            className="w-28 text-right"
            title="How actionable the paper is (high-fidelity notebook / sketch / findings only). Set by the LLM during enrichment; editable in the row detail."
          >
            Classification
          </span>
          <span
            className="w-20 text-right"
            title="LLM-scored relevance to PropAlgos users (high / medium / low)."
          >
            Relevance
          </span>
          <span
            className="w-20 text-right"
            title="Draft = admin-only. Published = visible in /research-feed."
          >
            Status
          </span>
          <span className="w-16" />
        </div>
      )}

      {props.insights.length === 0 ? (
        <p id="drafts-row-list" className="text-sm text-neutral-600">No {props.statusFilter === 'all' ? '' : props.statusFilter + ' '}insights for this week.</p>
      ) : (
        <div id="drafts-row-list" className="space-y-1">
          {props.insights.map(ins => (
            <InsightCard
              key={ins.public_id}
              insight={ins}
              expanded={props.expandedPid === ins.public_id}
              selected={props.selectedPids.has(ins.public_id)}
              onToggleExpand={() => props.setExpandedPid(props.expandedPid === ins.public_id ? null : ins.public_id)}
              onToggleSelect={() => toggleSelect(ins.public_id)}
              onSave={(patch) => props.savePatch(ins.public_id, patch)}
              onFlipStatus={(toPub) => props.flipStatus(ins.public_id, toPub)}
              onRegenerate={() => props.regenerate(ins.public_id)}
              onFilterBySource={(src) => props.setSourceFilter({
                publicId: src.public_id,
                label: src.display_name || src.publication_slug || src.subject || 'source email',
              })}
              onUndoAutoPublish={props.onUndoAutoPublish ? () => props.onUndoAutoPublish!(ins.public_id) : undefined}
              onClearFlag={props.onClearFlag ? () => props.onClearFlag!(ins.public_id) : undefined}
              onDelete={() => props.deleteOne(ins.public_id, ins.paper_title || '')}
              onRefetch={props.refetch}
            />
          ))}
        </div>
      )}

      {props.total > 0 && (
        <div className="mt-4 flex items-center gap-3 text-xs text-neutral-500">
          <button
            onClick={() => props.setOffset(Math.max(0, props.offset - props.pageSize))}
            disabled={!hasPrev}
            className="rounded-md border border-neutral-800 px-3 py-1.5 hover:border-neutral-700 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ← Prev
          </button>
          <button
            onClick={() => props.setOffset(props.offset + props.pageSize)}
            disabled={!hasNext}
            className="rounded-md border border-neutral-800 px-3 py-1.5 hover:border-neutral-700 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next →
          </button>
          <span>Page {Math.floor(props.offset / props.pageSize) + 1} of {Math.max(1, Math.ceil(props.total / props.pageSize))}</span>
          <div className="ml-auto flex items-center gap-2">
            <span>Rows per page</span>
            <select
              value={props.pageSize}
              onChange={(e) => props.setPageSize(parseInt(e.target.value, 10))}
              className="rounded-md border border-neutral-800 bg-neutral-950 px-2 py-1 text-xs text-neutral-300"
            >
              {[50, 100, 200, 500].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Display helpers (classification / relevance / source / date) ────

function ClassificationChip({ value }: { value: string | null }) {
  if (!value) {
    return <span className="inline-block text-[10px] text-neutral-600 italic">unclassified</span>;
  }
  const styles: Record<string, { label: string; className: string; title: string }> = {
    notebook_high_fidelity: {
      label: 'high-fidelity',
      className: 'bg-emerald-500/15 text-emerald-300',
      title: 'Notebook can be reconstructed from the paper with high fidelity.',
    },
    notebook_sketch: {
      label: 'sketch',
      className: 'bg-amber-500/15 text-amber-300',
      title: 'Only a notebook sketch — key details are missing from the paper.',
    },
    findings_only: {
      label: 'findings',
      className: 'bg-neutral-700/40 text-neutral-300',
      title: 'Findings-only — no runnable code recipe available.',
    },
  };
  const s = styles[value] || { label: value, className: 'bg-neutral-700/40 text-neutral-300', title: value };
  return (
    <span
      className={`inline-block px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide ${s.className}`}
      title={s.title}
    >
      {s.label}
    </span>
  );
}

function RelevanceChip({ value }: { value: string }) {
  const v = (value || '').toLowerCase();
  const styles: Record<string, string> = {
    high: 'bg-emerald-500/15 text-emerald-300',
    medium: 'bg-amber-500/15 text-amber-300',
    low: 'bg-neutral-700/40 text-neutral-400',
  };
  if (!v || v === 'none') {
    return <span className="inline-block text-[10px] text-neutral-600 italic">—</span>;
  }
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide ${styles[v] || 'bg-neutral-700/40 text-neutral-400'}`}>
      {v}
    </span>
  );
}

function formatShortDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function fidelityColor(score: number | null): string {
  if (score == null) return 'text-neutral-500';
  if (score >= 0.7) return 'text-emerald-300';
  if (score >= 0.4) return 'text-amber-300';
  return 'text-neutral-400';
}

// v3 — small ribbon under the row when the curator auto-routed it.
function CurationVerdictRibbon({ insight }: { insight: InsightRow }) {
  const verdict = insight.curation_verdict;
  if (!verdict || !verdict.decision) return null;
  const flagged = !!insight.flagged_for_review;
  const palette: Record<string, string> = {
    auto_publish: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200',
    auto_publish_flag: 'border-amber-500/40 bg-amber-500/10 text-amber-200',
    hold_for_review: 'border-neutral-700 bg-neutral-800/40 text-neutral-300',
    auto_dismiss: 'border-neutral-800 bg-neutral-900/60 text-neutral-500',
  };
  const label: Record<string, string> = {
    auto_publish: 'Auto-published',
    auto_publish_flag: flagged ? 'Auto-published · flagged' : 'Auto-published',
    hold_for_review: 'Held for review',
    auto_dismiss: 'Auto-dismissed',
  };
  const cls = palette[verdict.decision] || palette.hold_for_review;
  const verdictText = label[verdict.decision] || verdict.decision;
  const confPct = verdict.confidence != null ? Math.round(verdict.confidence * 100) : null;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-medium ${cls}`}
      title={`Curator: ${verdict.reason}\n\nGates passed: ${verdict.gates_passed.join(', ') || 'none'}${verdict.gates_failed.length ? '\nGates failed: ' + verdict.gates_failed.join(', ') : ''}`}
    >
      <span>{verdictText}</span>
      {confPct != null && <span className="text-[9px] opacity-70">{confPct}%</span>}
    </span>
  );
}


// v3 — Curation summary card. Shows this-week bucket counts at the top
// of the Inbox tab so admins see the full picture without trawling rows.
function CurationSummaryCard({
  weekIso,
  onJumpToInbox,
}: {
  weekIso: string;
  onJumpToInbox: () => void;
}) {
  const navigate = useNavigate();
  const [stats, setStats] = useState<CurationStatsResponse['stats'] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(
        `${getApiUrl()}/api/v1/admin/research/digest/preview`,
        { params: { week_iso: weekIso }, headers: authHeaders() },
      );
      setStats(r.data?.stats || null);
    } catch (err) {
      if (isAuthError(err, () => navigate('/login'))) return;
      setError('Failed to load curation stats');
    } finally {
      setLoading(false);
    }
  }, [weekIso, navigate]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (error) {
    return (
      <div className="mb-4 rounded-lg border border-red-700/40 bg-red-900/20 px-3 py-2 text-xs text-red-200">
        {error}
      </div>
    );
  }
  if (!stats && loading) {
    return <div className="mb-4 h-16 rounded-lg border border-neutral-800 bg-neutral-900/40 animate-pulse" />;
  }
  if (!stats) return null;

  const cells = [
    { key: 'auto_published', label: 'Auto-published', value: stats.auto_published, cls: 'text-emerald-200' },
    { key: 'flagged_for_review', label: 'Flagged for spot-check', value: stats.flagged_for_review, cls: 'text-amber-200' },
    { key: 'inbox', label: 'Inbox', value: stats.inbox, cls: 'text-indigo-200' },
    { key: 'auto_dismissed', label: 'Auto-dismissed', value: stats.auto_dismissed, cls: 'text-neutral-400' },
  ];
  return (
    <div className="mb-4 rounded-xl border border-neutral-800 bg-gradient-to-br from-indigo-950/30 to-neutral-950/0 p-4">
      <div className="mb-2 flex items-center justify-between">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-300/80">
            Curator activity · {weekIso}
          </p>
          <p className="text-xs text-neutral-500 mt-0.5">
            What the curator did vs. what's still on your plate.
          </p>
        </div>
        {stats.inbox > 0 && (
          <button
            type="button"
            onClick={onJumpToInbox}
            className="rounded-md border border-indigo-500/40 bg-indigo-500/10 px-3 py-1.5 text-xs font-medium text-indigo-200 hover:bg-indigo-500/20"
          >
            Triage {stats.inbox} →
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {cells.map(c => (
          <div key={c.key} className="rounded-lg border border-neutral-800 bg-neutral-950/60 p-2.5">
            <div className={`text-2xl font-semibold tabular-nums ${c.cls}`}>{c.value}</div>
            <div className="mt-0.5 text-[11px] text-neutral-500">{c.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}


// v3 — Bedrock-generated edit suggestions for an Inbox row.
// Lazy-loads on first open via the existing GET endpoint; admins click
// Apply / Dismiss; Refresh re-runs Bedrock.
function SuggestedEditsPanel({
  insightPublicId,
  onApplied,
}: {
  insightPublicId: string;
  onApplied: () => void;
}) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<SuggestionRow[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await axios.get(
        `${getApiUrl()}/api/v1/admin/research/insights/${insightPublicId}/suggestions`,
        { headers: authHeaders() },
      );
      setRows(r.data?.suggestions || []);
    } catch (err) {
      if (isAuthError(err, () => navigate('/login'))) return;
      setError('Failed to load suggestions');
    }
  }, [insightPublicId, navigate]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const r = await axios.post(
        `${getApiUrl()}/api/v1/admin/research/insights/${insightPublicId}/suggestions/refresh`,
        null,
        { headers: authHeaders() },
      );
      setRows(r.data?.suggestions || []);
    } catch (err) {
      if (isAuthError(err, () => navigate('/login'))) return;
      setError('Failed to refresh — Bedrock call may have errored. Try again in a moment.');
    } finally {
      setRefreshing(false);
    }
  };

  const apply = async (suggestion: SuggestionRow) => {
    setBusyId(suggestion.public_id);
    try {
      await axios.post(
        `${getApiUrl()}/api/v1/admin/research/insights/${insightPublicId}/suggestions/${suggestion.public_id}/apply`,
        null,
        { headers: authHeaders() },
      );
      setRows(r => (r ? r.filter(x => x.public_id !== suggestion.public_id) : r));
      onApplied();
    } catch (err) {
      if (isAuthError(err, () => navigate('/login'))) return;
      setError('Failed to apply suggestion');
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = async (suggestion: SuggestionRow) => {
    setBusyId(suggestion.public_id);
    try {
      await axios.post(
        `${getApiUrl()}/api/v1/admin/research/insights/${insightPublicId}/suggestions/${suggestion.public_id}/dismiss`,
        null,
        { headers: authHeaders() },
      );
      setRows(r => (r ? r.filter(x => x.public_id !== suggestion.public_id) : r));
    } catch (err) {
      if (isAuthError(err, () => navigate('/login'))) return;
      setError('Failed to dismiss suggestion');
    } finally {
      setBusyId(null);
    }
  };

  const renderProposed = (s: SuggestionRow): string => {
    if (s.field_name === 'feature_tags' && Array.isArray(s.proposed_value)) {
      return s.proposed_value.join(', ');
    }
    if (typeof s.proposed_value === 'string') return s.proposed_value;
    return JSON.stringify(s.proposed_value);
  };

  return (
    <div className="rounded-md border border-indigo-500/20 bg-indigo-950/20 px-3 py-2.5 text-xs">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-indigo-300/80">
          Suggested edits {rows && `(${rows.length})`}
        </span>
        <button
          type="button"
          onClick={refresh}
          disabled={refreshing}
          className="text-[10px] text-neutral-400 hover:text-indigo-300 disabled:opacity-50"
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      {error && <div className="mb-2 text-[11px] text-red-300">{error}</div>}
      {rows == null ? (
        <div className="text-[11px] text-neutral-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-[11px] text-neutral-500 italic">
          No suggestions. Click Refresh to ask Bedrock for one round of edit ideas.
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map(s => (
            <li key={s.public_id} className="rounded border border-neutral-800 bg-neutral-950/40 p-2">
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-mono text-[10px] uppercase tracking-wider text-indigo-300">
                  {s.field_name}
                </span>
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => apply(s)}
                    disabled={busyId === s.public_id}
                    className="rounded border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-50"
                  >
                    Apply
                  </button>
                  <button
                    type="button"
                    onClick={() => dismiss(s)}
                    disabled={busyId === s.public_id}
                    className="rounded border border-neutral-700 px-2 py-0.5 text-[10px] text-neutral-400 hover:text-neutral-200 disabled:opacity-50"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
              <div className="mt-1.5 text-[11px] text-neutral-300 break-words">
                {renderProposed(s)}
              </div>
              <div className="mt-1 text-[10px] italic text-neutral-500 break-words">
                {s.rationale}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function InsightCard(props: {
  insight: InsightRow;
  expanded: boolean;
  selected: boolean;
  onToggleExpand: () => void;
  onToggleSelect: () => void;
  onSave: (patch: Partial<InsightRow>) => Promise<void>;
  onFlipStatus: (toPublished: boolean) => Promise<void>;
  onRegenerate: () => Promise<void>;
  onFilterBySource: (src: InsightSourceEmail) => void;
  onUndoAutoPublish?: () => Promise<void>;
  onClearFlag?: () => Promise<void>;
  onDelete: () => Promise<void>;
  onRefetch: () => Promise<void>;
}) {
  const { insight } = props;
  const [draft, setDraft] = useState<Partial<InsightRow>>({});
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [actionJsonText, setActionJsonText] = useState<string>(JSON.stringify(insight.actionability_json || {}, null, 2));
  const [actionJsonErr, setActionJsonErr] = useState<string | null>(null);

  useEffect(() => {
    setDraft({});
    setActionJsonText(JSON.stringify(insight.actionability_json || {}, null, 2));
    setActionJsonErr(null);
  }, [insight.public_id, insight.updated_at]);

  const setField = <K extends keyof InsightRow>(k: K, v: InsightRow[K]) => {
    setDraft(d => ({ ...d, [k]: v }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const patch: Partial<InsightRow> = { ...draft };
      if (actionJsonText !== JSON.stringify(insight.actionability_json || {}, null, 2)) {
        try {
          patch.actionability_json = actionJsonText.trim() ? JSON.parse(actionJsonText) : null;
          setActionJsonErr(null);
        } catch (e: any) {
          setActionJsonErr('Invalid JSON');
          setSaving(false);
          return;
        }
      }
      await props.onSave(patch);
    } finally {
      setSaving(false);
    }
  };

  const isPublished = insight.status === 'published';

  const src = insight.source_email;
  const srcLabel = src ? (src.display_name || src.publication_slug || src.subject || 'source') : null;
  const paperPubShort = formatShortDate(insight.paper_published_at);
  const codeRefCount = Array.isArray(insight.code_references) ? insight.code_references.length : 0;

  return (
    <div className={`rounded-md border ${props.expanded ? 'border-neutral-700' : 'border-neutral-900 hover:border-neutral-800'} bg-neutral-900/30`}>
      <div className="flex items-center gap-3 px-3 py-2">
        <input type="checkbox" checked={props.selected} onChange={props.onToggleSelect} onClick={(e) => e.stopPropagation()} />
        <button onClick={props.onToggleExpand} className="flex-1 text-left text-sm truncate min-w-0">
          <div className="truncate">
            {insight.corroborated && (
              <span className="text-indigo-400 mr-1.5" title="Corroborated by multiple sources">★</span>
            )}
            {insight.paper_title || '(untitled)'}
          </div>
          {(paperPubShort || (insight.feature_tags && insight.feature_tags.length > 0) || insight.curation_verdict || (insight.previously_shown_weeks && insight.previously_shown_weeks.length > 0)) && (
            <div className="text-[10px] text-neutral-600 mt-0.5 flex items-center gap-2 truncate">
              {paperPubShort && <span title="Paper publication date">pub {paperPubShort}</span>}
              {insight.previously_shown_weeks && insight.previously_shown_weeks.length > 0 && (
                <span
                  className="px-1.5 py-px rounded bg-amber-900/40 border border-amber-700 text-amber-200 font-mono"
                  title={`Already published in ${insight.previously_shown_weeks.join(', ')}. Curator hard-routed to Bucket C; even if you republish, this paper cannot be a Top Pick.`}
                >
                  shown in {insight.previously_shown_weeks[0]}
                  {insight.previously_shown_weeks.length > 1 && ` +${insight.previously_shown_weeks.length - 1}`}
                </span>
              )}
              <CurationVerdictRibbon insight={insight} />
              {insight.feature_tags && insight.feature_tags.slice(0, 3).map((t, i) => (
                <span key={i} className="px-1 py-px rounded bg-neutral-800/60 text-neutral-400">{t}</span>
              ))}
              {insight.feature_tags && insight.feature_tags.length > 3 && (
                <span className="text-neutral-600">+{insight.feature_tags.length - 3}</span>
              )}
            </div>
          )}
        </button>
        <div className="w-40 text-right truncate text-[11px]">
          {src ? (
            <button
              onClick={(e) => { e.stopPropagation(); props.onFilterBySource(src); }}
              className="text-neutral-400 hover:text-indigo-300 truncate"
              title={`From ${srcLabel}${src.issue_date ? ' · ' + formatShortDate(src.issue_date) : ''} · click to filter to this source`}
            >
              {srcLabel}
              {src.issue_date && <span className="text-neutral-600"> · {formatShortDate(src.issue_date)}</span>}
            </button>
          ) : (
            <span className="text-neutral-700 italic">—</span>
          )}
        </div>
        <div className="w-28 text-right"><ClassificationChip value={insight.classification} /></div>
        <div className="w-20 text-right"><RelevanceChip value={insight.propalgos_relevance} /></div>
        <div className="w-20 text-right">
          <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wide ${isPublished ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`}>
            {insight.status}
          </span>
        </div>
        <div className="w-16 text-right">
          <button
            onClick={(e) => { e.stopPropagation(); props.onFlipStatus(!isPublished); }}
            className="text-xs text-neutral-400 hover:text-white"
          >
            {isPublished ? 'unpublish' : 'publish'}
          </button>
        </div>
      </div>

      {props.expanded && (
        <div className="border-t border-neutral-800 px-4 py-3 space-y-3 text-sm">
          {/* Job-outcome strip: what the ingestion produced for this paper. */}
          <div className="rounded-md border border-neutral-800 bg-neutral-950/60 px-3 py-2 text-xs">
            <div className="flex items-center gap-2 flex-wrap text-neutral-400">
              {src ? (
                <>
                  <span className="text-neutral-500">From</span>
                  <span className="text-neutral-200">{srcLabel}</span>
                  {src.issue_date && <><span className="text-neutral-700">·</span><span>{formatShortDate(src.issue_date)}</span></>}
                  {src.paper_count > 0 && (
                    <>
                      <span className="text-neutral-700">·</span>
                      <span>{src.paper_count} paper{src.paper_count === 1 ? '' : 's'} in batch</span>
                    </>
                  )}
                  <button
                    onClick={() => props.onFilterBySource(src)}
                    className="ml-auto rounded-md border border-neutral-800 px-2 py-1 text-[10px] text-neutral-400 hover:border-indigo-500/50 hover:text-indigo-300"
                  >
                    View all drafts from this email
                  </button>
                </>
              ) : (
                <span className="italic text-neutral-600">Source email not linked — this draft may predate audit-log source tracking.</span>
              )}
            </div>
            <div className="mt-2 flex items-center gap-4 text-[11px] text-neutral-500 flex-wrap">
              <span title="LLM-assessed fidelity of the actionability reconstruction (0.0–1.0).">
                Fidelity: <span className={fidelityColor(insight.fidelity_score)}>{insight.fidelity_score != null ? insight.fidelity_score.toFixed(2) : '—'}</span>
              </span>
              <span title="GitHub/GitLab code references discovered during enrichment.">
                Code refs: <span className="text-neutral-300">{codeRefCount}</span>
              </span>
              <span title="Date the paper itself was published — not when it was ingested.">
                Paper published: <span className="text-neutral-300">{paperPubShort || '—'}</span>
              </span>
              {insight.paper_url && (
                <a
                  href={insight.paper_url}
                  target="_blank"
                  rel="noreferrer"
                  className="ml-auto text-indigo-300 hover:text-indigo-200"
                >
                  Open paper ↗
                </a>
              )}
            </div>
          </div>

          <label className="block">
            <span className="text-xs uppercase tracking-wide text-neutral-500">Title</span>
            <input
              className="w-full mt-1 rounded border border-neutral-800 bg-neutral-950 px-2 py-1"
              value={draft.paper_title ?? insight.paper_title}
              onChange={(e) => setField('paper_title', e.target.value)}
            />
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-wide text-neutral-500">Summary (markdown)</span>
            <textarea
              className="w-full mt-1 rounded border border-neutral-800 bg-neutral-950 px-2 py-1 font-mono text-xs"
              rows={4}
              value={draft.summary_md ?? insight.summary_md ?? ''}
              onChange={(e) => setField('summary_md', e.target.value)}
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-wide text-neutral-500">Classification</span>
              <select
                className="w-full mt-1 rounded border border-neutral-800 bg-neutral-950 px-2 py-1"
                value={draft.classification ?? insight.classification ?? ''}
                onChange={(e) => setField('classification', e.target.value || null)}
              >
                <option value="">—</option>
                <option value="notebook_high_fidelity">notebook_high_fidelity</option>
                <option value="notebook_sketch">notebook_sketch</option>
                <option value="findings_only">findings_only</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wide text-neutral-500">Relevance</span>
              <select
                className="w-full mt-1 rounded border border-neutral-800 bg-neutral-950 px-2 py-1"
                value={draft.propalgos_relevance ?? insight.propalgos_relevance}
                onChange={(e) => setField('propalgos_relevance', e.target.value)}
              >
                <option value="high">high</option>
                <option value="medium">medium</option>
                <option value="low">low</option>
                <option value="none">none</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wide text-neutral-500">GPU tier</span>
              <input
                className="w-full mt-1 rounded border border-neutral-800 bg-neutral-950 px-2 py-1"
                value={draft.recommended_gpu_tier ?? insight.recommended_gpu_tier ?? ''}
                onChange={(e) => setField('recommended_gpu_tier', e.target.value || null)}
              />
            </label>
            <label className="flex items-center gap-2 mt-5">
              <input
                type="checkbox"
                checked={draft.corroborated ?? insight.corroborated}
                onChange={(e) => setField('corroborated', e.target.checked)}
              />
              <span className="text-sm">corroborated</span>
            </label>
          </div>

          <label className="block">
            <span className="text-xs uppercase tracking-wide text-neutral-500">Actionability JSON</span>
            <textarea
              className={`w-full mt-1 rounded border bg-neutral-950 px-2 py-1 font-mono text-xs ${actionJsonErr ? 'border-red-500' : 'border-neutral-800'}`}
              rows={10}
              value={actionJsonText}
              onChange={(e) => setActionJsonText(e.target.value)}
            />
            {actionJsonErr && <span className="text-xs text-red-400">{actionJsonErr}</span>}
          </label>

          {/* v3 — Bedrock-generated edit suggestions. Inbox-only — for
              published rows there's no value in showing the panel. */}
          {!isPublished && (
            <SuggestedEditsPanel
              insightPublicId={insight.public_id}
              onApplied={props.onRefetch}
            />
          )}

          <div className="flex items-center gap-3 pt-2 flex-wrap">
            <button
              onClick={save}
              disabled={saving}
              className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={async () => { setRegenerating(true); try { await props.onRegenerate(); } finally { setRegenerating(false); } }}
              disabled={regenerating}
              className="rounded-md border border-neutral-700 px-3 py-1.5 text-xs text-neutral-300 hover:bg-neutral-800 disabled:opacity-50"
            >
              {regenerating ? 'Regenerating…' : 'Regenerate actionability'}
            </button>
            <button
              onClick={() => props.onDelete()}
              className="ml-auto rounded-md border border-red-800/60 bg-red-900/20 px-3 py-1.5 text-xs text-red-300 hover:border-red-700 hover:bg-red-900/40"
              title="Hard-delete this row. Cannot be undone."
            >
              Delete
            </button>
            {/* v3 — admin override of the curator's auto-publish verdict.
                Visible only on rows that came from the curator (have a
                verdict.json) AND are still published. */}
            {isPublished && insight.curation_verdict && props.onUndoAutoPublish && (
              <button
                onClick={async () => {
                  if (!window.confirm('Move this auto-published row back to Inbox?')) return;
                  await props.onUndoAutoPublish?.();
                }}
                className="rounded-md border border-amber-700/50 bg-amber-900/20 px-3 py-1.5 text-xs text-amber-200 hover:border-amber-600 hover:bg-amber-900/40"
                title="Flips status to draft + records the override in the audit log."
              >
                Undo auto-publish
              </button>
            )}
            {insight.flagged_for_review && props.onClearFlag && (
              <button
                onClick={() => props.onClearFlag?.()}
                className="rounded-md border border-emerald-700/50 bg-emerald-900/20 px-3 py-1.5 text-xs text-emerald-200 hover:border-emerald-600 hover:bg-emerald-900/40"
                title="Approve and clear the spot-check flag."
              >
                Approve (clear flag)
              </button>
            )}
            <a href={insight.paper_url} target="_blank" rel="noreferrer" className="ml-auto text-xs text-neutral-500 hover:text-neutral-300">Open paper ↗</a>
          </div>

          {insight.audit_log && insight.audit_log.length > 0 && (
            <details className="text-xs text-neutral-500 mt-3">
              <summary className="cursor-pointer">Audit log ({insight.audit_log.length})</summary>
              <ul className="mt-2 space-y-1">
                {insight.audit_log.slice(0, 10).map((a, i) => (
                  <li key={i}>
                    <span className="text-neutral-400">{a.action}</span>
                    {' · '}
                    <span>{new Date(a.created_at).toLocaleString()}</span>
                    {' · '}
                    <span className="text-neutral-600">{a.actor_public_id.slice(0, 8)}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Weekly tab ─────────────────────────────────────────────────────

// ─── Weekly summary helpers ──────────────────────────────────────────

const STAGE_LABELS: Record<string, string> = {
  fetch_insights: 'Fetching insights',
  load_context: 'Loading platform context',
  fetch_news: 'Fetching news headlines',
  build_prompt: 'Building prompt',
  persist: 'Persisting summary',
};

function stageLabel(step: string): string {
  if (STAGE_LABELS[step]) return STAGE_LABELS[step];
  const m = /^bedrock_attempt_(\d+)$/.exec(step);
  if (m) return `Invoking Bedrock (attempt ${m[1]}/3)`;
  return step;
}

const ATTEMPT_OUTCOME_LABEL: Record<string, { label: string; cls: string }> = {
  success: { label: 'Succeeded', cls: 'text-emerald-300' },
  invoke_error: { label: 'Bedrock error', cls: 'text-red-300' },
  json_parse: { label: 'JSON parse failed', cls: 'text-red-300' },
  schema: { label: 'Schema validation failed', cls: 'text-red-300' },
  intro_quality: { label: 'Intro quality failed', cls: 'text-red-300' },
};

function shortModelName(modelId: string): string {
  // us.anthropic.claude-opus-4-6-v1 → Opus 4.6
  const m = /claude-(opus|sonnet|haiku)-(\d+)-(\d+)/.exec(modelId);
  if (m) {
    const tier = m[1].charAt(0).toUpperCase() + m[1].slice(1);
    return `${tier} ${m[2]}.${m[3]}`;
  }
  return modelId;
}

function formatDurationMs(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const s = Math.round(ms / 100) / 10;
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = Math.round(s - m * 60);
  return rem === 0 ? `${m}m` : `${m}m ${rem}s`;
}

function eventDurationMs(e: SummaryStageEvent): number | null {
  if (!e.started_at || !e.finished_at) return null;
  const a = new Date(e.started_at).getTime();
  const b = new Date(e.finished_at).getTime();
  if (isNaN(a) || isNaN(b)) return null;
  return Math.max(0, b - a);
}

function jobElapsedMs(job: WeeklySummaryJob): number | null {
  if (!job.started_at) return null;
  const a = new Date(job.started_at).getTime();
  if (isNaN(a)) return null;
  const b = job.completed_at ? new Date(job.completed_at).getTime() : Date.now();
  return Math.max(0, b - a);
}

function formatUsd(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

function StageRow({ event }: { event: SummaryStageEvent }) {
  const dur = eventDurationMs(event);
  const glyph = event.outcome === 'ok' ? '✓' : event.outcome === 'error' ? '✗' : '◔';
  const glyphCls = event.outcome === 'ok'
    ? 'text-emerald-400'
    : event.outcome === 'error'
      ? 'text-red-400'
      : 'text-neutral-500';
  return (
    <div className="flex items-start gap-3 px-3 py-2 border-b border-neutral-900 last:border-b-0">
      <span className={`${glyphCls} font-mono w-4`}>{glyph}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-neutral-200">{stageLabel(event.step)}</div>
        {event.detail && (
          <div className="text-xs text-neutral-500 mt-0.5 break-words">{event.detail}</div>
        )}
      </div>
      {dur != null && (
        <span className="text-[11px] text-neutral-500 tabular-nums whitespace-nowrap">
          {formatDurationMs(dur)}
        </span>
      )}
    </div>
  );
}

function ProgressPanel({
  job, onDismiss, onCancel,
}: {
  job: WeeklySummaryJob;
  onDismiss: () => void;
  onCancel: () => void;
}) {
  const isActive = job.status === 'pending' || job.status === 'running';
  const elapsed = jobElapsedMs(job);
  // Stable tick so elapsed time ticks every second while running.
  const [, forceRender] = useState(0);
  useEffect(() => {
    if (!isActive) return;
    const i = setInterval(() => forceRender(x => x + 1), 1000);
    return () => clearInterval(i);
  }, [isActive]);

  const statusPill = (() => {
    switch (job.status) {
      case 'pending': return { label: 'queued', cls: 'bg-neutral-700/40 text-neutral-300' };
      case 'running': return { label: 'running', cls: 'bg-indigo-500/15 text-indigo-300' };
      case 'completed': return { label: 'completed', cls: 'bg-emerald-500/15 text-emerald-300' };
      case 'failed': return { label: 'failed', cls: 'bg-red-500/15 text-red-300' };
      case 'cancelled': return { label: 'cancelled', cls: 'bg-neutral-700/40 text-neutral-400' };
    }
  })();
  const barColor = job.status === 'completed'
    ? 'bg-emerald-500'
    : job.status === 'failed'
      ? 'bg-red-500'
      : 'bg-indigo-500';

  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-900/40">
      <div className="flex items-center gap-3 px-3 py-2 border-b border-neutral-900">
        <span className="text-sm font-medium">Generating summary for {formatWeekRange(job.week_iso)}</span>
        <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wide ${statusPill.cls}`}>
          {statusPill.label}
        </span>
        {elapsed != null && (
          <span className="text-xs text-neutral-500 tabular-nums">
            {formatDurationMs(elapsed)} elapsed
          </span>
        )}
        <div className="ml-auto flex items-center gap-4 text-xs text-neutral-500">
          {job.input_paper_count != null && (
            <span title="Published + high/medium-relevance papers fed to the prompt.">
              {job.input_paper_count} papers
            </span>
          )}
          <span title="Cumulative token usage across all Bedrock attempts, successful + failed.">
            {formatNumber(job.total_input_tokens)} in · {formatNumber(job.total_output_tokens)} out
          </span>
          <span
            className="text-neutral-300"
            title="Estimated cost based on AWS Bedrock on-demand list prices. Actual billed cost may differ."
          >
            {formatUsd(job.total_cost_cents)} est
          </span>
          {isActive ? (
            <button
              onClick={() => {
                const ok = window.confirm('Cancel this generation? Any in-flight Bedrock call will keep running server-side but the job will be marked cancelled.');
                if (ok) onCancel();
              }}
              className="rounded border border-neutral-700 px-2 py-0.5 text-[10px] uppercase tracking-wide text-neutral-400 hover:border-red-500/50 hover:text-red-300"
              title="Force-cancel this run. Useful when the job is orphaned (backend restarted mid-run) and stuck in 'running' forever."
            >
              Cancel
            </button>
          ) : (
            <button
              onClick={onDismiss}
              className="text-neutral-500 hover:text-red-300 text-lg leading-none px-1"
              title="Dismiss this job from the list. Generated summary is unaffected."
              aria-label="Dismiss job"
            >×</button>
          )}
        </div>
      </div>
      <div className="h-1.5 bg-neutral-800 overflow-hidden">
        <div className={`h-full ${barColor} transition-all`} style={{ width: `${job.progress_pct}%` }} />
      </div>
      {/* Stage timeline. Events arrive append-only from the backend so we
          just render them in order. Backend emits one row per stage or
          Bedrock attempt, so there's no dedup needed. */}
      <div>
        {job.stage_events.length === 0 ? (
          <div className="px-3 py-3 text-xs text-neutral-500">Waiting for first stage…</div>
        ) : (
          job.stage_events.map((e, i) => <StageRow key={i} event={e} />)
        )}
      </div>
      {/* Per-attempt detail cards (collapsed into a sub-section so the
          timeline stays scannable). Always show if >= 1 attempt, so the
          admin can see tokens + cost even on the first success. */}
      {job.attempts.length > 0 && (
        <div className="border-t border-neutral-900 px-3 py-2 space-y-1.5">
          <div className="text-[10px] uppercase tracking-wider text-neutral-600">Bedrock attempts</div>
          {job.attempts.map((a, i) => {
            const outcome = ATTEMPT_OUTCOME_LABEL[a.outcome] || { label: a.outcome, cls: 'text-neutral-400' };
            return (
              <div key={i} className="rounded border border-neutral-900 bg-neutral-950/60 px-2.5 py-1.5 text-xs">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-neutral-400">#{a.attempt}</span>
                  <span className="text-neutral-200">{shortModelName(a.model_id)}</span>
                  <span className={outcome.cls}>{outcome.label}</span>
                  <span className="ml-auto text-neutral-500 tabular-nums">
                    {formatNumber(a.tokens_input)} + {formatNumber(a.tokens_output)} tok · {formatUsd(a.cost_cents)}
                  </span>
                </div>
                {a.error_detail && (
                  <div className="text-[11px] text-red-200 mt-1 font-mono whitespace-pre-wrap break-words">
                    {a.error_detail}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* Top-level error bubble (separate from per-attempt details so
          the 400-ish "no insights" case surfaces plainly). */}
      {job.status === 'failed' && job.error_message && (
        <div className="border-t border-red-500/30 bg-red-500/5 px-3 py-2 text-xs text-red-200 whitespace-pre-wrap break-words">
          {job.error_message}
        </div>
      )}
    </div>
  );
}

function WeeklyTab(props: {
  weekIso: string;
  readiness: ReadinessResponse | null;
  preflight: PreflightResponse | null;
  preflightLoading: boolean;
  onRefreshPreflight: () => void;
  onForcePublish: (stripLinks: boolean) => Promise<void>;
  summary: any | null;
  setSummary: (s: any) => void;
  busy: 'idle' | 'publishing' | 'saving';
  error: string | null;
  activeJob: WeeklySummaryJob | null;
  jobHistory: WeeklySummaryJob[];
  onGenerate: () => Promise<void>;
  onSave: () => Promise<void>;
  onPublish: () => Promise<void>;
  onJumpToDrafts: () => void;
  onDismissJob: (publicId: string) => Promise<void>;
  onCancelJob: (publicId: string) => Promise<void>;
}) {
  const { summary, readiness, preflight, activeJob, jobHistory } = props;
  const isPublished = summary?.status === 'published';
  const isGenerating = activeJob?.status === 'pending' || activeJob?.status === 'running';

  // Force-publish escape hatch. `intro_quality` is the ONLY failure
  // outcome that leaves a recoverable parsed_payload on the job's
  // attempts (structural failures — json_parse/schema/invoke_error —
  // have nothing to ship, and the backend 409s on them). Hidden once
  // the week's letter is published or a new generation is in flight.
  const showForcePublish = !isPublished
    && !isGenerating
    && preflight?.last_letter_outcome === 'intro_quality';
  const [confirmForce, setConfirmForce] = useState(false);
  const [stripLinks, setStripLinks] = useState(true);
  useEffect(() => { setConfirmForce(false); }, [props.weekIso]);
  // Latest terminal job for this week — used for the post-run summary
  // card (cost, tokens, time) once generation finishes.
  const latestTerminal = [activeJob, ...jobHistory]
    .filter((j): j is WeeklySummaryJob => !!j && (j.status === 'completed' || j.status === 'failed'))
    .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))[0] || null;
  // Past runs (exclude the one shown at the top).
  const pastRuns = jobHistory.filter(j => !activeJob || j.public_id !== activeJob.public_id);

  const updateSection = (path: string, value: unknown) => {
    if (!summary?.content) return;
    const next = { ...summary, content: { ...summary.content, [path]: value } };
    props.setSummary(next);
  };

  return (
    <div className="space-y-5">
      <TabGuidance
        title="Weekly summary — write the editorial letter"
        purpose="Generate the Monday editorial letter from this week's published insights. Opus drafts the intro + themes + per-pick narratives; you edit, then publish to the public Research Feed."
        steps={[
          'Read the preflight card first — fix any red ✗ blockers before generating. Yellow ⚠ items publish fine but are flagged for a reason.',
          'Wait until the readiness card shows 0 drafts (everything published or rejected). Generating with drafts in flight produces a letter that ignores them.',
          'Click "Generate weekly summary". The job runs ~1–2 min on Opus 4.6 (with 4.5 fallback) and shows per-stage progress. Cost typically $0.50–$2 per generation.',
          'Edit the intro / themes / blurbs inline. The intro requires ≥2 distinct headline links — the validator will reject and retry on its own if it fails.',
          'Click "Publish" to push it to the user-facing Research Feed. Editing post-publish is allowed; every change is audit-logged.',
        ]}
        tone="violet"
      />

      <PreflightCard
        preflight={preflight}
        loading={props.preflightLoading}
        onRefresh={props.onRefreshPreflight}
      />

      {readiness && (
        <div className={`rounded-md p-3 ${readiness.draft_count > 0 ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-emerald-500/10 border border-emerald-500/30'}`}>
          <div className="text-sm">
            <span className="font-medium">{formatWeekRange(readiness.week_iso)}</span>
            {' · '}
            {readiness.published_count} published · {readiness.draft_count} draft
          </div>
          {readiness.recommendation && (
            <div className="text-xs text-amber-200 mt-1">
              {readiness.recommendation}
              {' '}
              <button onClick={props.onJumpToDrafts} className="underline hover:text-white">review drafts →</button>
            </div>
          )}
        </div>
      )}

      {/* Active / latest job panel. Shows progress while running; stays
          visible after completion so admins see cost + stage breakdown. */}
      {(activeJob || latestTerminal) && (
        <ProgressPanel
          job={activeJob || latestTerminal!}
          onDismiss={() => {
            const pid = (activeJob || latestTerminal)!.public_id;
            props.onDismissJob(pid);
          }}
          onCancel={() => {
            const pid = (activeJob || latestTerminal)!.public_id;
            props.onCancelJob(pid);
          }}
        />
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={props.onGenerate}
          disabled={isGenerating}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
          title={
            isGenerating
              ? 'A generation job is already running for this week.'
              : summary
                ? 'Regenerate — replaces the current draft. Previous attempt remains visible in run history.'
                : 'Run Opus across this week\'s published high/medium-relevance insights and emit a draft editorial summary.'
          }
        >
          {isGenerating ? 'Generating…' : summary ? 'Regenerate' : 'Generate summary'}
        </button>
        {summary && (
          <>
            <button
              onClick={props.onSave}
              disabled={props.busy !== 'idle' || isGenerating}
              className="rounded-md border border-neutral-700 px-4 py-2 text-sm text-neutral-300 hover:bg-neutral-800 disabled:opacity-50"
            >
              {props.busy === 'saving' ? 'Saving…' : isPublished ? 'Save edits (hot-patch live)' : 'Save draft edits'}
            </button>
            <button
              onClick={props.onPublish}
              disabled={props.busy !== 'idle' || isPublished || isGenerating}
              className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
            >
              {props.busy === 'publishing' ? 'Publishing…' : isPublished ? 'Published' : 'Publish'}
            </button>
            {summary.status && (
              <span className={`text-xs ${isPublished ? 'text-emerald-400' : 'text-amber-400'}`}>
                {summary.status}
              </span>
            )}
          </>
        )}
        {showForcePublish && !confirmForce && (
          <button
            onClick={() => setConfirmForce(true)}
            disabled={props.busy !== 'idle'}
            className="rounded-md border border-amber-600/60 bg-amber-600/10 px-4 py-2 text-sm font-medium text-amber-300 hover:bg-amber-600/20 disabled:opacity-50"
            title="Last generation burned all intro-validation attempts. Ship the last structurally-valid draft as-is — no Bedrock re-spend."
          >
            Publish anyway…
          </button>
        )}
        {showForcePublish && confirmForce && (
          <div className="flex items-center gap-3 rounded-md border border-amber-600/50 bg-amber-950/30 px-3 py-1.5 flex-wrap">
            <span className="text-xs text-amber-200">Publishes without the intro quality gate.</span>
            <label className="flex items-center gap-1.5 text-xs text-amber-200/80 cursor-pointer">
              <input
                type="checkbox"
                checked={stripLinks}
                onChange={(e) => setStripLinks(e.target.checked)}
                className="accent-amber-500"
              />
              strip news-hook links from intro
            </label>
            <button
              onClick={() => { setConfirmForce(false); props.onForcePublish(stripLinks); }}
              disabled={props.busy !== 'idle'}
              className="rounded bg-amber-600 px-3 py-1 text-xs font-medium text-white hover:bg-amber-500 disabled:opacity-50"
            >
              {props.busy === 'publishing' ? 'Publishing…' : 'Confirm publish'}
            </button>
            <button
              onClick={() => setConfirmForce(false)}
              className="text-xs text-neutral-400 hover:text-neutral-200"
            >
              Cancel
            </button>
          </div>
        )}
        {props.error && <span className="text-sm text-red-400">{props.error}</span>}
      </div>

      {summary && isPublished && (
        <div className="text-xs text-neutral-500 -mt-2">
          This summary is live on the feed. Edits save immediately without changing status — no re-publish needed.
        </div>
      )}

      {summary?.content && (
        <div className="space-y-4">
          <SectionEditor
            label="Intro"
            value={summary.content.intro ?? ''}
            rows={3}
            onChange={(v) => updateSection('intro', v)}
          />
          <SectionEditor
            label="Closing nudge"
            value={summary.content.closing_nudge ?? ''}
            rows={2}
            onChange={(v) => updateSection('closing_nudge', v)}
          />
          <ListSectionEditor
            label="Excited to try"
            items={summary.content.excited_to_try ?? []}
            onChange={(items) => updateSection('excited_to_try', items)}
          />
          <ListSectionEditor
            label="Still early"
            items={summary.content.still_early ?? []}
            onChange={(items) => updateSection('still_early', items)}
          />
          <DatasetSectionEditor
            label="New datasets"
            items={summary.content.new_datasets ?? []}
            onChange={(items) => updateSection('new_datasets', items)}
          />
          <SectionEditor
            label="Themes (comma-separated)"
            value={(summary.content.themes ?? []).join(', ')}
            rows={1}
            onChange={(v) => updateSection('themes', v.split(',').map((s: string) => s.trim()).filter(Boolean))}
          />
        </div>
      )}

      {/* Past runs. Small history list showing every generation attempt
          for this week — useful for auditing opex. Dismiss (×) removes
          a row from this list without touching the summary content. */}
      {pastRuns.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wider text-neutral-600 mb-2">Past runs</div>
          <div className="space-y-1.5">
            {pastRuns.map(j => {
              const elapsed = jobElapsedMs(j);
              const pill = j.status === 'completed'
                ? { label: 'completed', cls: 'bg-emerald-500/15 text-emerald-300' }
                : j.status === 'failed'
                  ? { label: 'failed', cls: 'bg-red-500/15 text-red-300' }
                  : { label: j.status, cls: 'bg-neutral-700/40 text-neutral-300' };
              return (
                <div key={j.public_id} className="rounded-md border border-neutral-900 bg-neutral-950/40 px-3 py-2 text-xs flex items-center gap-3">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide ${pill.cls}`}>
                    {pill.label}
                  </span>
                  <span className="text-neutral-500">
                    {j.created_at && new Date(j.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </span>
                  {elapsed != null && (
                    <span className="text-neutral-600 tabular-nums">{formatDurationMs(elapsed)}</span>
                  )}
                  <span className="text-neutral-500 tabular-nums">
                    {formatNumber(j.total_input_tokens)} + {formatNumber(j.total_output_tokens)} tok
                  </span>
                  <span className="text-neutral-400 tabular-nums">{formatUsd(j.total_cost_cents)}</span>
                  <span className="text-neutral-600 truncate flex-1">
                    {j.status === 'failed' ? (j.error_message || 'Failed') : `${j.attempts.length} attempt${j.attempts.length === 1 ? '' : 's'}`}
                  </span>
                  {(j.status === 'completed' || j.status === 'failed') && (
                    <button
                      onClick={() => props.onDismissJob(j.public_id)}
                      className="text-neutral-600 hover:text-red-300 leading-none"
                      title="Dismiss this run from the list."
                      aria-label="Dismiss run"
                    >×</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Preflight card (Gate 5 go/no-go ribbon) ────────────────────────
//
// Renders GET /weekly-summary/{week}/preflight as a 30-second ✓/⚠/✗
// checklist. Verdict pill: green = all clear, amber = publish allowed
// but flagged, red = at least one blocker (can_publish=false). Fetched
// on demand only — tab mount, week change, the re-run button, and
// after generate/publish actions. Never polled.

const PREFLIGHT_GLYPH: Record<PreflightCheck['severity'], { glyph: string; cls: string }> = {
  ok: { glyph: '✓', cls: 'text-emerald-400' },
  warn: { glyph: '⚠', cls: 'text-amber-400' },
  block: { glyph: '✗', cls: 'text-red-400' },
};

function PreflightCard(props: {
  preflight: PreflightResponse | null;
  loading: boolean;
  onRefresh: () => void;
}) {
  const { preflight, loading } = props;

  if (!preflight) {
    // First load, or the fetch failed (auth errors bounce to /login
    // upstream). Keep the slot quiet rather than fabricating a verdict.
    return (
      <div className="rounded-md border border-neutral-800 bg-neutral-900/40 p-3.5 flex items-center justify-between">
        <span className="text-sm text-neutral-500">
          {loading ? 'Running preflight checks…' : 'Preflight unavailable — re-run to retry.'}
        </span>
        <button
          onClick={props.onRefresh}
          disabled={loading}
          className="rounded border border-neutral-700 px-2.5 py-1 text-xs text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800 disabled:opacity-50"
        >
          {loading ? 'Checking…' : 'Re-run checks'}
        </button>
      </div>
    );
  }

  const hasWarn = preflight.checks.some(c => c.severity === 'warn');
  const verdict = !preflight.can_publish
    ? { label: 'Blocked', pill: 'bg-red-500/15 text-red-300', frame: 'border-red-500/30 bg-red-500/5' }
    : hasWarn
      ? { label: 'Publish with warnings', pill: 'bg-amber-500/15 text-amber-300', frame: 'border-amber-500/30 bg-amber-500/5' }
      : { label: 'Ready to publish', pill: 'bg-emerald-500/15 text-emerald-300', frame: 'border-emerald-500/30 bg-emerald-500/5' };

  const { a, b, c } = preflight.bucket_counts;

  return (
    <div className={`rounded-md border p-3.5 ${verdict.frame}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className={`px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide ${verdict.pill}`}>
          {verdict.label}
        </span>
        <span className="text-sm font-medium text-neutral-200">Publish preflight</span>
        <span className="text-xs text-neutral-500">
          {a}A · {b}B · {c}C
          {preflight.inbox_aged_count > 0 && ` · ${preflight.inbox_aged_count} aged >24h`}
          {preflight.last_letter_attempts > 0
            && ` · last letter: ${preflight.last_letter_outcome ?? 'unknown'} (${preflight.last_letter_attempts} attempt${preflight.last_letter_attempts === 1 ? '' : 's'})`}
        </span>
        <div className="flex-1" />
        <button
          onClick={props.onRefresh}
          disabled={loading}
          className="rounded border border-neutral-700 px-2.5 py-1 text-xs text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800 disabled:opacity-50"
        >
          {loading ? 'Checking…' : 'Re-run checks'}
        </button>
      </div>

      <div className="mt-2.5 space-y-1">
        {preflight.checks.map((check, i) => {
          const g = PREFLIGHT_GLYPH[check.severity];
          return (
            <div key={`${check.label}-${i}`} className="flex items-start gap-2 text-xs">
              <span className={`w-4 shrink-0 text-center font-semibold ${g.cls}`}>{g.glyph}</span>
              <span className="text-neutral-300 font-medium whitespace-nowrap">{check.label}</span>
              {check.detail && <span className="text-neutral-500">{check.detail}</span>}
            </div>
          );
        })}
      </div>

      {preflight.top_picks.length > 0 && (
        <div className="mt-2.5 pt-2.5 border-t border-neutral-800/60 flex flex-wrap gap-1.5">
          {preflight.top_picks.map(pick => {
            const nbBroken = pick.notebook_validator_status === 'broken';
            const nbUnknown = !pick.notebook_validator_status || pick.notebook_validator_status === 'unknown';
            return (
              <span
                key={pick.insight_public_id}
                className="inline-flex items-center gap-1.5 rounded border border-neutral-800 bg-neutral-950/50 px-2 py-1 text-[11px] text-neutral-400"
                title={[
                  pick.infographic_error ? `infographic: ${pick.infographic_error}` : null,
                  pick.notebook_validator_errors.length > 0 ? `notebook: ${pick.notebook_validator_errors.join('; ')}` : null,
                ].filter(Boolean).join('\n') || undefined}
              >
                <span className="truncate max-w-[16rem]">{pick.headline || pick.insight_public_id.slice(0, 8)}</span>
                <span className={pick.has_infographic ? 'text-emerald-400' : 'text-amber-400'}>
                  {pick.has_infographic ? 'img✓' : 'img—'}
                </span>
                <span className={nbBroken ? 'text-red-400' : nbUnknown ? 'text-neutral-600' : 'text-emerald-400'}>
                  {nbBroken ? 'nb✗' : nbUnknown ? 'nb?' : 'nb✓'}
                </span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SectionEditor(props: { label: string; value: string; rows: number; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wide text-neutral-500">{props.label}</span>
      <textarea
        className="w-full mt-1 rounded border border-neutral-800 bg-neutral-950 px-3 py-2 text-sm"
        rows={props.rows}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
      />
    </label>
  );
}

function ListSectionEditor(props: { label: string; items: Array<{ insight_public_id?: string; blurb?: string }>; onChange: (items: any[]) => void }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-neutral-500">{props.label}</div>
      <div className="space-y-2 mt-1">
        {props.items.map((item, i) => (
          <div key={i} className="rounded border border-neutral-800 bg-neutral-950 p-2">
            <input
              className="w-full text-[11px] font-mono text-neutral-500 bg-transparent border-b border-neutral-900 pb-1 mb-1"
              value={item.insight_public_id ?? ''}
              onChange={(e) => {
                const next = [...props.items];
                next[i] = { ...next[i], insight_public_id: e.target.value };
                props.onChange(next);
              }}
              placeholder="insight_public_id"
            />
            <textarea
              className="w-full text-sm bg-transparent"
              rows={2}
              value={item.blurb ?? ''}
              onChange={(e) => {
                const next = [...props.items];
                next[i] = { ...next[i], blurb: e.target.value };
                props.onChange(next);
              }}
            />
            <button
              onClick={() => props.onChange(props.items.filter((_, j) => j !== i))}
              className="text-[11px] text-neutral-500 hover:text-red-400 mt-1"
            >
              remove
            </button>
          </div>
        ))}
        <button
          onClick={() => props.onChange([...props.items, { insight_public_id: '', blurb: '' }])}
          className="text-xs text-neutral-500 hover:text-neutral-300"
        >
          + add
        </button>
      </div>
    </div>
  );
}

function DatasetSectionEditor(props: { label: string; items: Array<{ token?: string; paper_count?: number; blurb?: string; insight_public_ids?: string[] }>; onChange: (items: any[]) => void }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-neutral-500">{props.label}</div>
      <div className="space-y-2 mt-1">
        {props.items.map((item, i) => (
          <div key={i} className="rounded border border-neutral-800 bg-neutral-950 p-2">
            <div className="flex gap-2 mb-1">
              <input
                className="flex-1 text-xs font-mono text-neutral-300 bg-transparent border border-neutral-900 rounded px-2 py-0.5"
                value={item.token ?? ''}
                onChange={(e) => {
                  const next = [...props.items];
                  next[i] = { ...next[i], token: e.target.value };
                  props.onChange(next);
                }}
                placeholder="dataset-token"
              />
              <input
                type="number"
                className="w-20 text-xs text-neutral-300 bg-transparent border border-neutral-900 rounded px-2 py-0.5"
                value={item.paper_count ?? 0}
                onChange={(e) => {
                  const next = [...props.items];
                  next[i] = { ...next[i], paper_count: parseInt(e.target.value, 10) || 0 };
                  props.onChange(next);
                }}
              />
            </div>
            <textarea
              className="w-full text-sm bg-transparent"
              rows={2}
              value={item.blurb ?? ''}
              onChange={(e) => {
                const next = [...props.items];
                next[i] = { ...next[i], blurb: e.target.value };
                props.onChange(next);
              }}
            />
            <button
              onClick={() => props.onChange(props.items.filter((_, j) => j !== i))}
              className="text-[11px] text-neutral-500 hover:text-red-400 mt-1"
            >
              remove
            </button>
          </div>
        ))}
        <button
          onClick={() => props.onChange([...props.items, { token: '', paper_count: 0, blurb: '', insight_public_ids: [] }])}
          className="text-xs text-neutral-500 hover:text-neutral-300"
        >
          + add
        </button>
      </div>
    </div>
  );
}

// ─── Top Picks tab ──────────────────────────────────────────────────
//
// Renders the published weekly letter's `top_picks` array with per-pick
// edit fields and the two admin actions: Regenerate Infographic
// (Bedrock spec → AOAI gpt-image render) and Reseed Canonical Notebook
// (RepoSeederService re-run for the linked repo).

interface TopPickEntry {
  insight_public_id: string;
  headline: string;
  narrative_md: string;
  infographic_brief: string;
}

function TopPicksTab(props: {
  weekIso: string;
  summary: any | null;
  setSummary: (s: any) => void;
  onSaveSummary: () => Promise<void>;
}) {
  const { weekIso, summary, setSummary, onSaveSummary } = props;
  const picks: TopPickEntry[] = (summary?.content?.top_picks ?? []) as TopPickEntry[];
  const [insightById, setInsightById] = useState<Record<string, InsightRow>>({});
  const [busyByPid, setBusyByPid] = useState<Record<string, 'regen' | 'reseed' | null>>({});
  const [errorByPid, setErrorByPid] = useState<Record<string, string | null>>({});
  const [saving, setSaving] = useState(false);

  const fetchInsightForPick = useCallback(async (pid: string) => {
    try {
      const r = await axios.get<InsightRow>(
        `${getApiUrl()}/api/v1/admin/research/insights/${pid}`,
        { headers: authHeaders() },
      );
      setInsightById(prev => ({ ...prev, [pid]: r.data }));
    } catch (_err) { /* leave stale; UI degrades to "no insight" view */ }
  }, []);

  useEffect(() => {
    // Fetch once per pick when the picks list changes. No polling — admin
    // explicitly triggers refresh via Regenerate.
    for (const p of picks) {
      if (!insightById[p.insight_public_id]) fetchInsightForPick(p.insight_public_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picks.map(p => p.insight_public_id).join(',')]);

  const updatePickField = (idx: number, field: keyof TopPickEntry, value: string) => {
    if (!summary?.content) return;
    const nextPicks = picks.map((p, i) => i === idx ? { ...p, [field]: value } : p);
    setSummary({ ...summary, content: { ...summary.content, top_picks: nextPicks } });
  };

  const handleSave = async () => {
    setSaving(true);
    try { await onSaveSummary(); } finally { setSaving(false); }
  };

  const regenerate = async (pid: string) => {
    setBusyByPid(prev => ({ ...prev, [pid]: 'regen' }));
    setErrorByPid(prev => ({ ...prev, [pid]: null }));
    try {
      await axios.post(
        `${getApiUrl()}/api/v1/admin/research/weekly-summary/${weekIso}/regenerate-infographic`,
        { insight_public_id: pid },
        { headers: authHeaders() },
      );
      await fetchInsightForPick(pid);
    } catch (err: any) {
      setErrorByPid(prev => ({ ...prev, [pid]: err?.response?.data?.detail ?? 'Regenerate failed' }));
    } finally {
      setBusyByPid(prev => ({ ...prev, [pid]: null }));
    }
  };

  const reseed = async (pid: string) => {
    if (!window.confirm('Re-clone the repo and rebuild the canonical notebook? This calls Bedrock and may take ~30s.')) return;
    setBusyByPid(prev => ({ ...prev, [pid]: 'reseed' }));
    setErrorByPid(prev => ({ ...prev, [pid]: null }));
    try {
      await axios.post(
        `${getApiUrl()}/api/v1/admin/research/insights/${pid}/reseed-canonical-notebook`,
        {},
        { headers: authHeaders() },
      );
    } catch (err: any) {
      setErrorByPid(prev => ({ ...prev, [pid]: err?.response?.data?.detail ?? 'Reseed failed' }));
    } finally {
      setBusyByPid(prev => ({ ...prev, [pid]: null }));
    }
  };

  if (!summary) {
    return (
      <div className="text-sm text-neutral-500">
        No weekly summary loaded for {weekIso}. Generate one in the Weekly Summary tab first.
      </div>
    );
  }

  if (picks.length === 0) {
    return (
      <div className="text-sm text-neutral-500">
        No top picks selected for {weekIso}. The Top-3 selector found no high-tier
        runnable papers — re-run the weekly summary after publishing more drafts,
        or accept the empty slot if no papers qualified this week.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <TabGuidance
        title="Top Picks — the visual lead of the letter"
        purpose="Edit the 1–3 cards that headline the public Research Feed: hero infographic + 2-line headline + 200–600 char narrative. The selector picks them deterministically; you tune the words and visuals."
        steps={[
          'Per pick: review the auto-generated headline + narrative_md. Tighten anything that reads generic — these are the only words most users will see.',
          'Edit infographic_brief if the auto-spec doesn\'t capture the finding right, then click Regenerate to re-call the image model. Cached on spec hash, so identical briefs reuse the existing PNG.',
          'Click "Reseed canonical notebook" if the pick has notebook_repo_seed classification — runs the repo seeder once per (paper, repo, commit) and uploads the .ipynb to shared S3.',
          'Save changes when done. The picks publish with the rest of the weekly letter; no separate publish step.',
        ]}
        tone="emerald"
      />

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-neutral-200">Top Picks — {weekIso}</h3>
          <p className="text-xs text-neutral-500 mt-0.5">
            {picks.length} pick{picks.length !== 1 ? 's' : ''} selected by the deterministic Top-3 filter.
            Edit per-pick text below; Save to persist into the published letter.
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 rounded text-white"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      <div className="space-y-4">
        {picks.map((p, idx) => {
          const insight = insightById[p.insight_public_id];
          const busy = busyByPid[p.insight_public_id];
          const error = errorByPid[p.insight_public_id];
          return (
            <div key={p.insight_public_id} className="border border-neutral-800 rounded-lg p-4 bg-neutral-900/40">
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-4">
                  <div className="aspect-video bg-neutral-950 border border-neutral-800 rounded overflow-hidden flex items-center justify-center">
                    {insight?.infographic_url ? (
                      <img
                        src={insight.infographic_url}
                        alt={`Infographic for pick ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-xs text-neutral-600">no infographic yet</span>
                    )}
                  </div>
                  <div className="text-[11px] text-neutral-500 mt-2 break-all">
                    {insight?.paper_title || p.insight_public_id}
                  </div>
                  {insight?.paper_url && (
                    <a
                      href={insight.paper_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] text-indigo-400 hover:text-indigo-300"
                    >
                      open paper ↗
                    </a>
                  )}
                </div>

                <div className="col-span-12 md:col-span-8 space-y-3">
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-wide text-neutral-500">Headline (40–120 chars)</span>
                    <input
                      type="text"
                      value={p.headline}
                      onChange={(e) => updatePickField(idx, 'headline', e.target.value)}
                      className="w-full mt-1 px-2 py-1.5 text-sm bg-neutral-950 border border-neutral-800 rounded text-neutral-200"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-wide text-neutral-500">Narrative (200–600 chars, markdown)</span>
                    <textarea
                      rows={4}
                      value={p.narrative_md}
                      onChange={(e) => updatePickField(idx, 'narrative_md', e.target.value)}
                      className="w-full mt-1 px-2 py-1.5 text-sm bg-neutral-950 border border-neutral-800 rounded text-neutral-200 font-mono"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-wide text-neutral-500">Infographic brief (60–250 chars — drives Pass-1 spec)</span>
                    <textarea
                      rows={3}
                      value={p.infographic_brief}
                      onChange={(e) => updatePickField(idx, 'infographic_brief', e.target.value)}
                      className="w-full mt-1 px-2 py-1.5 text-sm bg-neutral-950 border border-neutral-800 rounded text-neutral-200"
                    />
                  </label>

                  <div className="flex items-center gap-2 pt-1">
                    <button
                      onClick={() => regenerate(p.insight_public_id)}
                      disabled={!!busy}
                      className="px-3 py-1.5 text-xs bg-neutral-800 hover:bg-neutral-700 disabled:opacity-50 rounded text-neutral-200"
                    >
                      {busy === 'regen' ? 'Regenerating…' : 'Regenerate infographic'}
                    </button>
                    <button
                      onClick={() => reseed(p.insight_public_id)}
                      disabled={!!busy}
                      className="px-3 py-1.5 text-xs bg-neutral-800 hover:bg-neutral-700 disabled:opacity-50 rounded text-neutral-200"
                    >
                      {busy === 'reseed' ? 'Reseeding…' : 'Reseed canonical notebook'}
                    </button>
                  </div>

                  {error && (
                    <div className="text-xs text-red-400 mt-1">{error}</div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Pipeline Runs tab ──────────────────────────────────────────────
//
// Test/prod pipeline-run wizard with per-stage timing, bucket
// distribution, and split-brain prevention. The same backend code path
// services both modes — test runs cap papers at sample_size and write
// to a `{week}-TEST-{shortpid}` sentinel week so they're isolated from
// prod queries. Promote-to-prod requires a green test within 60min on
// the same admin identity.

interface PipelineRunResponse {
  run_public_id: string;
  week_iso: string;
  sources: string[];
  generate_specs: boolean;
  status: string;
  is_test_run: boolean;
  sample_size: number | null;
  parent_job_public_id: string | null;
}

const STAGE_ORDER = [
  'discovery', 'normalize', 'persist_papers', 'enrichment',
  'score_corroborate', 'notebook_specs', 'drafts',
];

interface FixtureStatus {
  slug: string;
  filename: string;
  display_name: string;
  file_exists: boolean;
  file_size: number;
  status: 'ready' | 'ingesting' | 'failed' | 'not_ingested' | 'missing_file';
  insight_count: number;
  ingested_at: string | null;
  week_iso: string;
}

function PipelineRunsTab(props: {
  weekIso: string;
  onJumpToDrafts: (testWeekIso: string) => void;
  // ── Newsletter ingestion (.eml upload + recent jobs) ────────────
  // Lives at page scope so the same state powers the upload UI here
  // and the polling for active ingestion runs.
  jobs: JobResponse[];
  uploading: boolean;
  uploadError: string | null;
  dragActive: boolean;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onFilePick: () => void;
  fileInputRef: React.MutableRefObject<HTMLInputElement | null>;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDismissJob: (publicId: string) => Promise<void>;
  onCancelJob: (publicId: string) => Promise<void>;
  onClearFailed: () => Promise<void>;
  onJumpToDrafts2: (job: JobResponse) => void;
  onIngestionStarted: () => void;
  // ── Lifted state — survives tab switches ────────────────────────
  activeJobPid: string | null;
  setActiveJobPid: React.Dispatch<React.SetStateAction<string | null>>;
  activeJob: JobWithTimings | null;
  setActiveJob: React.Dispatch<React.SetStateAction<JobWithTimings | null>>;
  insights: InsightRow[];
  setInsights: React.Dispatch<React.SetStateAction<InsightRow[]>>;
  summaryPreview: any | null;
  setSummaryPreview: React.Dispatch<React.SetStateAction<any | null>>;
}) {
  const { weekIso, onJumpToDrafts } = props;
  const [sources, setSources] = useState<Set<string>>(new Set(['arxiv']));
  const [sampleSize, setSampleSize] = useState<number>(15);
  const [generateSpecs, setGenerateSpecs] = useState<boolean>(true);
  // activeJobPid / activeJob / insights / summaryPreview now lifted via props.
  const activeJobPid = props.activeJobPid;
  const setActiveJobPid = props.setActiveJobPid;
  const activeJob = props.activeJob;
  const setActiveJob = props.setActiveJob;
  const insights = props.insights;
  const setInsights = props.setInsights;
  const summaryPreview = props.summaryPreview;
  const setSummaryPreview = props.setSummaryPreview;
  const [recentJobs, setRecentJobs] = useState<JobWithTimings[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'idle' | 'starting-test' | 'starting-prod' | 'cleaning' | 'fixtures'>('idle');
  const [promoteError, setPromoteError] = useState<string | null>(null);
  const [cleanupResult, setCleanupResult] = useState<string | null>(null);
  const [keepTestSummaryOnPromote, setKeepTestSummaryOnPromote] = useState<boolean>(true);
  // v3.4 — bundled fixture newsletters (Justina + ML Quant) for the
  // current base week. The "Use fixture newsletters" button stamps both
  // .eml files at the current week so the email-merge stage finds them
  // on the next test run.
  const [fixtures, setFixtures] = useState<FixtureStatus[]>([]);
  const [fixtureError, setFixtureError] = useState<string | null>(null);

  const fetchFixtures = useCallback(async () => {
    try {
      const r = await axios.get<{ fixtures: FixtureStatus[] }>(
        `${getApiUrl()}/api/v1/admin/research/test-fixtures/newsletters?week_iso=${encodeURIComponent(weekIso)}`,
        { headers: authHeaders() },
      );
      setFixtures(r.data.fixtures || []);
    } catch (_e) {
      setFixtures([]);
    }
  }, [weekIso]);

  useEffect(() => { fetchFixtures(); }, [fetchFixtures]);

  // Re-poll fixtures when an ingestion job completes — covers both the
  // "Use fixture newsletters" path and a manual .eml drop of a known
  // fixture filename. Cheap GET, only fires on transitions.
  useEffect(() => {
    const anyIngesting = props.jobs.some(j => !['completed', 'failed', 'cancelled'].includes(j.status));
    if (anyIngesting) {
      const handle = setInterval(fetchFixtures, 4000);
      return () => clearInterval(handle);
    }
    // One-shot refresh after the queue drains.
    fetchFixtures();
    return undefined;
  }, [props.jobs, fetchFixtures]);

  const ingestFixtures = async () => {
    setBusy('fixtures');
    setFixtureError(null);
    try {
      await axios.post(
        `${getApiUrl()}/api/v1/admin/research/test-fixtures/ingest-newsletters?week_iso=${encodeURIComponent(weekIso)}`,
        {},
        { headers: authHeaders() },
      );
      // Refresh both the fixture status (so the UI flips to "ingesting")
      // and the page-level jobs list (so the new email-ingestion jobs
      // appear immediately in the Recent panel without waiting for the
      // 2s poll cycle).
      fetchFixtures();
      props.onIngestionStarted();
    } catch (err: any) {
      setFixtureError(err?.response?.data?.detail ?? 'Fixture ingest failed');
    } finally {
      setBusy('idle');
    }
  };

  const toggleSource = (s: string) => {
    const next = new Set(sources);
    if (next.has(s)) next.delete(s); else next.add(s);
    setSources(next);
  };

  const fetchRecent = useCallback(async () => {
    try {
      const r = await axios.get<JobWithTimings[]>(
        `${getApiUrl()}/api/v1/admin/research/jobs?limit=15`,
        { headers: authHeaders() },
      );
      // Filter to discovery jobs only; show test runs prominently
      const filtered = (r.data || []).filter(j => j.kind === 'discovery');
      setRecentJobs(filtered);
    } catch (_e) { /* noop */ }
  }, []);

  const fetchActive = useCallback(async (pid: string) => {
    try {
      const r = await axios.get<JobWithTimings>(
        `${getApiUrl()}/api/v1/admin/research/jobs/${pid}`,
        { headers: authHeaders() },
      );
      setActiveJob(r.data);
      // Once the job completes, fetch the resulting insights for the run's week_iso.
      if (r.data.status === 'completed' || r.data.status === 'failed') {
        if (r.data.status === 'completed' && r.data.week_iso) {
          try {
            const ins = await axios.get<{ insights: InsightRow[]; total: number }>(
              `${getApiUrl()}/api/v1/admin/research/insights?week_iso=${encodeURIComponent(r.data.week_iso)}&limit=50`,
              { headers: authHeaders() },
            );
            setInsights(ins.data.insights || []);
          } catch (_e) { /* noop */ }
          // v3.3 — fetch the test-mode weekly summary preview if it exists.
          // Only test runs generate one; prod skips this stage. 404 is expected
          // when summary generation failed or there were 0 eligible insights.
          if (r.data.is_test_run) {
            try {
              const sum = await axios.get<any>(
                `${getApiUrl()}/api/v1/research-insights/admin/weekly-summary/${encodeURIComponent(r.data.week_iso)}`,
                { headers: authHeaders() },
              );
              setSummaryPreview(sum.data || null);
            } catch (_e) {
              setSummaryPreview(null);
            }
          } else {
            setSummaryPreview(null);
          }
        }
      }
    } catch (_e) { /* noop */ }
  }, []);

  // Poll active job every 2s until terminal. Always fires once on
  // mount so a completed run repopulates its insights/preview after a
  // tab switch — the lifted activeJobPid sticks around but lifted
  // insights/preview may have been cleared by a different action;
  // fetchActive re-fills them when status is terminal.
  useEffect(() => {
    if (!activeJobPid) return;
    if (activeJob && ['completed', 'failed', 'cancelled'].includes(activeJob.status)) {
      // Already terminal — fetch once to refresh insights/preview if
      // they got cleared, then exit. Don't keep polling a finished job.
      fetchActive(activeJobPid);
      return;
    }
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      await fetchActive(activeJobPid);
    };
    tick();
    const handle = setInterval(() => { if (!cancelled) tick(); }, 2000);
    return () => { cancelled = true; clearInterval(handle); };
  // We intentionally only re-arm on activeJobPid change — relying on
  // activeJob.status here would tear down the interval after every
  // poll tick that updates the snapshot.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJobPid, fetchActive]);

  // Refresh the recent-runs list once the active job hits a terminal
  // state, so the row picks up its final status without waiting on the
  // next manual remount.
  useEffect(() => {
    if (activeJob && (activeJob.status === 'completed' || activeJob.status === 'failed' || activeJob.status === 'cancelled')) {
      fetchRecent();
    }
  }, [activeJob?.status, fetchRecent]);

  useEffect(() => { fetchRecent(); }, [fetchRecent]);

  const startRun = async (mode: 'test' | 'prod') => {
    setError(null);
    setPromoteError(null);
    setBusy(mode === 'test' ? 'starting-test' : 'starting-prod');
    try {
      if (mode === 'prod') {
        // Prod runs require promote-to-prod from a recent green test.
        // For a direct prod run (no test), reject — split-brain prevention.
        setError('Prod runs must be promoted from a successful test run within the last 60 minutes. Click "Promote to prod" on a green test below.');
        return;
      }
      const body = {
        week_iso: weekIso,
        sources: Array.from(sources),
        generate_specs: generateSpecs,
        is_test_run: true,
        sample_size: sampleSize,
      };
      const r = await axios.post<PipelineRunResponse>(
        `${getApiUrl()}/api/v1/admin/research/discover-week`,
        body,
        { headers: authHeaders() },
      );
      setActiveJobPid(r.data.run_public_id);
      setActiveJob(null);
      setInsights([]);
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? `Failed to start ${mode} run`);
    } finally {
      setBusy('idle');
    }
  };

  const promoteToProd = async (testJobPid: string, keepSummary: boolean) => {
    const intro = keepSummary
      ? 'Promote this test run to a full prod run AND keep this test\'s summary content (intro, themes, top-pick narratives) for the prod feed?'
      : 'Promote this test run to a full prod run? This will process the entire week\'s firehose (~5-10 min) and write real insights. Prod summary will be regenerated fresh.';
    if (!window.confirm(intro)) return;
    setPromoteError(null);
    setBusy('starting-prod');
    try {
      const r = await axios.post<PipelineRunResponse>(
        `${getApiUrl()}/api/v1/admin/research/promote-to-prod`,
        { test_job_public_id: testJobPid, keep_test_summary: keepSummary },
        { headers: authHeaders() },
      );
      setActiveJobPid(r.data.run_public_id);
      setActiveJob(null);
      setInsights([]);
      setSummaryPreview(null);
    } catch (err: any) {
      setPromoteError(err?.response?.data?.detail ?? 'Promote failed');
    } finally {
      setBusy('idle');
    }
  };

  const cleanupTestData = async () => {
    if (!window.confirm('Delete all test-run insights + orphan papers + jobs older than 24h? Prod data is untouched.')) return;
    setBusy('cleaning');
    setCleanupResult(null);
    try {
      const r = await axios.post<{ deleted_test_insights: number; deleted_test_papers: number; deleted_test_jobs: number }>(
        `${getApiUrl()}/api/v1/admin/research/test-runs/cleanup?older_than_hours=24`,
        {},
        { headers: authHeaders() },
      );
      setCleanupResult(`Deleted ${r.data.deleted_test_insights} insights · ${r.data.deleted_test_papers} orphan papers · ${r.data.deleted_test_jobs} jobs`);
      fetchRecent();
    } catch (err: any) {
      setCleanupResult(`Failed: ${err?.response?.data?.detail ?? err?.message}`);
    } finally {
      setBusy('idle');
    }
  };

  const cancelActiveJob = async () => {
    if (!activeJob) return;
    if (!window.confirm(`Cancel the active ${activeJob.is_test_run ? 'test' : 'prod'} run? The pipeline checks for cancellation between stages, so it'll bail at the next checkpoint (typically before the next Bedrock call).`)) return;
    try {
      const r = await axios.post<JobWithTimings>(
        `${getApiUrl()}/api/v1/admin/research/jobs/${activeJob.public_id}/cancel`,
        {},
        { headers: authHeaders() },
      );
      setActiveJob(r.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? 'Cancel failed');
    }
  };

  // Derive bucket distribution from completed insights.
  const buckets = useMemo(() => {
    const result: Record<string, number> = { auto_publish: 0, auto_publish_flag: 0, hold_for_review: 0, dismissed: 0 };
    for (const ins of insights) {
      const decision = ins.curation_verdict?.decision;
      if (decision === 'auto_publish') result.auto_publish++;
      else if (decision === 'auto_publish_flag') result.auto_publish_flag++;
      else if (decision === 'hold_for_review' || ins.status === 'draft') result.hold_for_review++;
      else if (decision === 'auto_dismiss') result.dismissed++;
    }
    return result;
  }, [insights]);

  const topPickCandidates = useMemo(() => {
    return insights
      .filter(i => (i.classification === 'notebook_high_fidelity' || i.classification === 'notebook_sketch' || i.classification === 'notebook_repo_seed')
        && i.propalgos_relevance === 'high'
        && (i.fidelity_score ?? 0) >= 0.6)
      .sort((a, b) => (b.fidelity_score ?? 0) - (a.fidelity_score ?? 0))
      .slice(0, 5);
  }, [insights]);

  const promoteEligible = activeJob?.is_test_run && activeJob.status === 'completed';

  return (
    <div className="space-y-6">
      <TabGuidance
        title="Pipeline Runs — your weekly entry point"
        purpose="Run the curator end-to-end so the rest of the editor has something to triage. A test run is a small sample (default 15 papers) you eyeball before paying for the full prod run."
        steps={[
          'Pick sources + sample size, then "Run test". Watch per-stage timings fill in left-to-right; if any stage stalls, cancel and re-run.',
          'When test completes, scan the bucket distribution + the 25-row preview list below. Look for: do classifications look sensible? Are top-pick candidates actually runnable?',
          'If yes → "Promote to prod run" (no sample cap, ~5–10 min). If the test produced 0 papers, it usually means sources have no fresh content for this week yet — try last week or a different source mix.',
          'Use "Use fixture newsletters" to seed the test pool with the bundled .eml files when discovery sources are thin (e.g. early in the week).',
        ]}
        tone="indigo"
      />

      <div className="border border-neutral-800 rounded-lg p-4 bg-neutral-900/40">
        <div className="mb-3">
          <h3 className="text-base font-semibold text-neutral-200">Run pipeline</h3>
          <p className="text-xs text-neutral-500 mt-0.5">
            Test (~1–2 min, sample of papers). Promote to prod (~5–10 min, full week) once results look right.
          </p>
        </div>

        <div className="grid grid-cols-12 gap-4">
          <label className="col-span-3">
            <span className="text-[11px] uppercase tracking-wide text-neutral-500">Week</span>
            <div className="mt-1 px-2 py-1.5 text-sm bg-neutral-950 border border-neutral-800 rounded text-neutral-300 font-mono">
              {weekIso}
            </div>
          </label>
          <div className="col-span-5">
            <span className="text-[11px] uppercase tracking-wide text-neutral-500">Sources</span>
            <div className="mt-1 flex gap-2">
              {(['arxiv', 'ssrn', 'nber'] as const).map(s => (
                <button
                  key={s}
                  onClick={() => toggleSource(s)}
                  className={`px-3 py-1.5 text-xs rounded border ${
                    sources.has(s)
                      ? 'bg-indigo-600/20 border-indigo-500 text-indigo-300'
                      : 'bg-neutral-950 border-neutral-800 text-neutral-500 hover:text-neutral-300'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <label className="col-span-2">
            <span className="text-[11px] uppercase tracking-wide text-neutral-500">Sample size (test)</span>
            <input
              type="number" min={1} max={100} value={sampleSize}
              onChange={(e) => setSampleSize(Math.max(1, Math.min(100, parseInt(e.target.value) || 15)))}
              className="w-full mt-1 px-2 py-1.5 text-sm bg-neutral-950 border border-neutral-800 rounded text-neutral-200 font-mono"
            />
          </label>
          <div className="col-span-2" />
        </div>

        <div className="flex items-center gap-3 mt-4">
          <button
            onClick={() => startRun('test')}
            disabled={busy !== 'idle' || sources.size === 0}
            className="px-4 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 rounded text-white"
          >
            {busy === 'starting-test' ? 'Starting test…' : `Run test (sample=${sampleSize})`}
          </button>
          <span className="text-[11px] text-neutral-500">
            Prod runs are gated — finish a test, then click <span className="text-neutral-300">Promote to prod</span> on the active-run card.
          </span>
        </div>
        {error && <div className="mt-2 text-xs text-red-400">{error}</div>}
        {promoteError && <div className="mt-2 text-xs text-red-400">Promote: {promoteError}</div>}
        {cleanupResult && <div className="mt-2 text-xs text-neutral-400">{cleanupResult}</div>}
      </div>

      {/* Newsletter ingestion — drag-drop + bundled fixtures.
          Test runs auto-merge any email-ingested rows for the base week
          into the test sentinel, so admin can validate the combined
          (discovery + email) view in one click. */}
      <div className="border border-neutral-800 rounded-lg p-4 bg-neutral-900/40">
        <div className="mb-3">
          <h3 className="text-base font-semibold text-neutral-200">Newsletter ingestion</h3>
          <p className="text-xs text-neutral-500 mt-0.5">
            Drop a .eml or seed the bundled fixtures. Email-ingested rows are stamped at the
            <span className="font-mono text-neutral-300"> {weekIso} </span>
            base week and the next test run's email-merge stage copies them into the test sentinel.
          </p>
        </div>

        {/* Bundled fixtures — Justina + ML Quant — for fast test loops. */}
        <div className="mb-4">
          <div className="flex items-baseline justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wide text-neutral-500">Bundled fixtures</span>
            <button
              onClick={ingestFixtures}
              disabled={busy !== 'idle' || props.uploading}
              className="px-3 py-1 text-xs font-medium bg-violet-600 hover:bg-violet-500 disabled:opacity-50 rounded text-white"
              title={`Re-ingest both .eml fixtures and stamp them at ${weekIso}.`}
            >
              {busy === 'fixtures' ? 'Queuing…' : `Use fixture newsletters (${weekIso})`}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {fixtures.length === 0 ? (
              <div className="col-span-2 text-xs text-neutral-600 italic">Loading fixtures…</div>
            ) : fixtures.map(fx => {
              const statusColor =
                fx.status === 'ready' ? 'border-emerald-700 bg-emerald-900/15 text-emerald-300'
                : fx.status === 'ingesting' ? 'border-indigo-700 bg-indigo-900/15 text-indigo-300'
                : fx.status === 'failed' ? 'border-rose-700 bg-rose-900/15 text-rose-300'
                : fx.status === 'missing_file' ? 'border-amber-700 bg-amber-900/15 text-amber-300'
                : 'border-neutral-700 bg-neutral-900/30 text-neutral-400';
              return (
                <div key={fx.slug} className={`rounded p-2 border ${statusColor}`}>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-xs font-medium truncate">{fx.display_name}</span>
                    <span className="text-[10px] uppercase tracking-wide font-mono shrink-0">{fx.status.replace('_', ' ')}</span>
                  </div>
                  <div className="mt-1 text-[11px] font-mono opacity-80">
                    {fx.status === 'ready'
                      ? `${fx.insight_count} insights @ ${fx.week_iso}`
                      : fx.status === 'missing_file'
                      ? `not found in ideas/`
                      : `${(fx.file_size / 1024).toFixed(0)}KB · ${fx.filename}`}
                  </div>
                </div>
              );
            })}
          </div>
          {fixtureError && (
            <div className="mt-2 text-xs text-red-300">{fixtureError}</div>
          )}
        </div>

        {/* Manual .eml drop — same handler as before. */}
        <div className="text-[11px] uppercase tracking-wide text-neutral-500 mb-2">Or upload manually</div>
        <div
          onDragOver={props.onDragOver}
          onDragLeave={props.onDragLeave}
          onDrop={props.onDrop}
          onClick={props.onFilePick}
          className={`cursor-pointer rounded border-2 border-dashed p-6 text-center transition ${
            props.dragActive
              ? 'border-indigo-400 bg-indigo-500/5'
              : 'border-neutral-800 bg-neutral-950 hover:border-neutral-600'
          }`}
        >
          <div className="text-xs text-neutral-400">
            {props.uploading ? 'Uploading…' : 'Drag and drop .eml files here, or click to browse'}
          </div>
          <div className="text-[11px] text-neutral-600 mt-1">
            Multiple files OK. Each runs in parallel (cap 3). Week is parsed from the email — use the fixture button above to stamp at {weekIso} instead.
          </div>
          <input
            ref={props.fileInputRef}
            type="file"
            accept=".eml"
            multiple
            hidden
            onChange={props.onFileChange}
          />
        </div>
        {props.uploadError && (
          <div className="mt-2 text-xs text-red-300 bg-red-900/30 border border-red-800/50 rounded px-2 py-1.5">
            {props.uploadError}
          </div>
        )}

        {/* Recent ingestion jobs — email/.eml only; discovery runs live in the
            "Recent runs" panel below with their own bucket-distribution cards. */}
        {(() => {
          const emailJobs = props.jobs.filter(j => j.kind === 'email');
          if (emailJobs.length === 0) return null;
          return (
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[11px] uppercase tracking-wide text-neutral-500">Recent email-ingestion jobs</span>
                {emailJobs.some(j => j.status === 'failed') && (
                  <button
                    onClick={props.onClearFailed}
                    className="ml-auto text-[11px] text-neutral-500 hover:text-red-300"
                  >
                    clear failed
                  </button>
                )}
              </div>
              <div className="space-y-1.5">
                {emailJobs.slice(0, 6).map(job => (
                  <JobRow
                    key={job.public_id}
                    job={job}
                    onDismiss={() => props.onDismissJob(job.public_id)}
                    onCancel={() => props.onCancelJob(job.public_id)}
                    onJumpToDrafts={() => props.onJumpToDrafts2(job)}
                  />
                ))}
              </div>
            </div>
          );
        })()}
      </div>

      {activeJob && (
        <div className="border border-neutral-800 rounded-lg p-4 bg-neutral-900/40">
          <div className="flex items-baseline justify-between mb-2">
            <h3 className="text-base font-semibold text-neutral-200">
              Active run · <span className="font-mono text-xs text-neutral-500">{activeJob.public_id}</span>
            </h3>
            <div className="flex items-center gap-2">
              {!['completed', 'failed', 'cancelled'].includes(activeJob.status) && (
                <button
                  onClick={cancelActiveJob}
                  className="px-2.5 py-0.5 text-[11px] font-medium bg-red-900/40 hover:bg-red-900/60 border border-red-700 rounded text-red-200"
                >
                  Cancel
                </button>
              )}
              <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wide rounded ${
                activeJob.status === 'completed' ? 'bg-emerald-900/40 text-emerald-300 border border-emerald-700' :
                activeJob.status === 'failed' ? 'bg-red-900/40 text-red-300 border border-red-700' :
                activeJob.status === 'cancelled' ? 'bg-amber-900/40 text-amber-300 border border-amber-700' :
                'bg-indigo-900/40 text-indigo-300 border border-indigo-700'
              }`}>
                {activeJob.status} · {activeJob.is_test_run ? `TEST (n=${activeJob.sample_size})` : 'PROD'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-6 gap-3 text-xs text-neutral-400 mb-3">
            <div><span className="text-neutral-600">week</span><br /><span className="font-mono text-neutral-300">{activeJob.week_iso}</span></div>
            <div><span className="text-neutral-600">sources</span><br /><span className="font-mono text-neutral-300">{activeJob.sources?.join(',') || '—'}</span></div>
            <div><span className="text-neutral-600">papers total</span><br /><span className="font-mono text-neutral-300">{activeJob.papers_total}</span></div>
            <div><span className="text-neutral-600">enriched</span><br /><span className="font-mono text-neutral-300">{activeJob.papers_enriched}</span></div>
            <div><span className="text-neutral-600">progress</span><br /><span className="font-mono text-neutral-300">{activeJob.progress_pct}%</span></div>
            <div><span className="text-neutral-600">step</span><br /><span className="font-mono text-neutral-300">{activeJob.current_step || '—'}</span></div>
          </div>

          <div className="w-full h-1.5 bg-neutral-800 rounded overflow-hidden mb-4">
            <div className="h-full bg-indigo-500 transition-all" style={{ width: `${activeJob.progress_pct}%` }} />
          </div>

          {/* Per-stage timings */}
          <div>
            <div className="text-[11px] uppercase tracking-wide text-neutral-500 mb-2">Per-stage timings (seconds)</div>
            <div className="flex flex-wrap gap-2">
              {STAGE_ORDER.map(stage => {
                const t = activeJob.stage_timings?.[stage];
                const done = typeof t === 'number';
                return (
                  <div key={stage} className={`px-3 py-1.5 text-xs rounded border ${
                    done ? 'bg-emerald-900/20 border-emerald-700 text-emerald-300' : 'bg-neutral-900 border-neutral-800 text-neutral-600'
                  }`}>
                    <span className="font-mono">{stage}</span>
                    <span className="ml-2 font-mono text-[11px]">{done ? `${t.toFixed(1)}s` : '…'}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {activeJob.error_message && (
            <div className="mt-3 p-2 bg-red-900/20 border border-red-700 rounded text-xs text-red-300 font-mono">
              {activeJob.error_message}
            </div>
          )}

          {promoteEligible && (
            <div className="mt-4 pt-4 border-t border-neutral-800">
              <button
                onClick={() => promoteToProd(activeJob.public_id, keepTestSummaryOnPromote)}
                disabled={busy !== 'idle'}
                className="px-4 py-2 text-sm font-medium bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 rounded text-white"
              >
                {busy === 'starting-prod' ? 'Promoting…' : 'Promote to prod run'}
              </button>
              <label className="ml-3 inline-flex items-center gap-2 text-xs text-neutral-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={keepTestSummaryOnPromote}
                  onChange={(e) => setKeepTestSummaryOnPromote(e.target.checked)}
                  className="accent-emerald-500"
                />
                Keep this intro on prod
                <span className="text-neutral-600">{summaryPreview ? '' : '(no preview yet)'}</span>
              </label>
              <p className="text-[11px] text-neutral-500 mt-1">
                Same sources + week, no sample cap. Window expires 60min after test completion.
                {keepTestSummaryOnPromote
                  ? ' Prod will inherit this test\'s intro/themes/narratives (top-pick public_ids remapped by paper_url).'
                  : ' Prod will regenerate the summary fresh via Bedrock.'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Empty-result interpretation — when a run completes with 0 papers
          the user otherwise sees an "Active run · COMPLETED" card with
          all timings as "..." and no idea what happened. Make it
          explicit. */}
      {activeJob?.status === 'completed' && insights.length === 0 && (
        <div className="border border-amber-700/60 rounded-lg p-4 bg-amber-950/20 text-sm">
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-base font-semibold text-amber-200">Run completed but produced 0 insights</span>
            <span className="text-[10px] uppercase tracking-wide font-mono text-amber-400">
              papers_total={activeJob.papers_total} · enriched={activeJob.papers_enriched}
            </span>
          </div>
          <p className="text-amber-100/80 mb-3">
            Discovery returned <span className="font-mono">{activeJob.papers_total}</span> papers from
            <span className="font-mono"> {activeJob.sources?.join(', ')}</span> for week
            <span className="font-mono"> {activeJob.week_iso}</span>. This is normal for a test early in the
            ISO week (sources publish on a delay) or when the chosen sources happen to have nothing fresh.
          </p>
          <div className="text-amber-100/70 text-xs">
            <strong className="text-amber-200">Common fixes:</strong>
            <ul className="list-disc list-inside mt-1 space-y-0.5">
              <li>Try the <em>previous</em> week — most arXiv crops post Mon/Tue UTC.</li>
              <li>Add <span className="font-mono">ssrn</span> + <span className="font-mono">nber</span> if you only had arxiv selected.</li>
              <li>Click "Use fixture newsletters" above to seed Justina + ML Quant emails into this week, then re-run the test (the email-merge stage will pull them into the test sentinel).</li>
              <li>If discovery returned 0 every time across multiple weeks, check backend logs for arXiv OAI-PMH / SSRN connector errors.</li>
            </ul>
          </div>
        </div>
      )}

      {/* Results inspector — shown once a job completes */}
      {activeJob?.status === 'completed' && insights.length > 0 && (
        <div className="border border-neutral-800 rounded-lg p-4 bg-neutral-900/40">
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="text-base font-semibold text-neutral-200">Results — bucket distribution</h3>
            <button
              onClick={() => onJumpToDrafts(activeJob.week_iso || '')}
              className="px-3 py-1.5 text-xs font-medium bg-indigo-900/40 hover:bg-indigo-900/60 border border-indigo-700 rounded text-indigo-200"
              title="Open the Drafts tab filtered to this test run's week. Lets you inspect each row's full content + verdict + suggested edits."
            >
              View {insights.length} drafts in Drafts tab →
            </button>
          </div>

          <div className="grid grid-cols-4 gap-3 mb-4">
            <BucketCell label="A — auto-publish" count={buckets.auto_publish} color="emerald" total={insights.length} />
            <BucketCell label="B — auto + flag" count={buckets.auto_publish_flag} color="amber" total={insights.length} />
            <BucketCell label="C — hold (Inbox)" count={buckets.hold_for_review} color="indigo" total={insights.length} />
            <BucketCell label="D — dismissed" count={buckets.dismissed} color="rose" total={insights.length} />
          </div>

          {/* Inline list of every produced insight — title + categorization + verdict.
              Top-Pick candidates are shown separately below if they qualified. */}
          <div className="mb-4">
            <div className="text-[11px] uppercase tracking-wide text-neutral-500 mb-2">
              All {insights.length} insights produced (click row → view full detail)
            </div>
            <div className="space-y-1.5">
              {insights.map((ins) => {
                const decisionColor =
                  ins.curation_verdict?.decision === 'auto_publish' ? 'text-emerald-400'
                  : ins.curation_verdict?.decision === 'auto_publish_flag' ? 'text-amber-400'
                  : ins.curation_verdict?.decision === 'auto_dismiss' ? 'text-rose-400'
                  : 'text-indigo-400';
                return (
                  <div
                    key={ins.public_id}
                    onClick={() => onJumpToDrafts(activeJob.week_iso || '')}
                    className="border border-neutral-800 rounded p-2.5 bg-neutral-950 hover:bg-neutral-900 cursor-pointer"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="text-sm text-neutral-200 truncate flex-1 flex items-center gap-2">
                        {ins.source_email && (
                          <span
                            className="px-1.5 py-px rounded bg-violet-900/40 border border-violet-700 text-violet-200 text-[10px] font-mono shrink-0"
                            title={`From email: ${ins.source_email.display_name || ins.source_email.publication_slug || ins.source_email.subject || 'newsletter'}`}
                          >
                            📧 email
                          </span>
                        )}
                        <span className="truncate">{ins.paper_title}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[11px] font-mono shrink-0">
                        <span className="text-neutral-500">{(ins.propalgos_relevance || 'none').toUpperCase()}</span>
                        <span className="text-neutral-600">·</span>
                        <span className="text-neutral-400">{ins.classification || 'unclassified'}</span>
                        {ins.fidelity_score != null && (
                          <>
                            <span className="text-neutral-600">·</span>
                            <span className="text-neutral-400">f={ins.fidelity_score.toFixed(2)}</span>
                          </>
                        )}
                      </div>
                    </div>
                    {ins.curation_verdict?.reason && (
                      <div className="mt-1 text-[11px] text-neutral-500">
                        verdict: <span className={`font-mono ${decisionColor}`}>{ins.curation_verdict.decision}</span>
                        <span className="ml-2 text-neutral-400">{ins.curation_verdict.reason}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wide text-neutral-500 mb-2">
              Top-Pick candidates (high relevance + runnable + fidelity ≥ 0.6) — top 5
            </div>
            {topPickCandidates.length === 0 ? (
              <div className="text-xs text-neutral-500 italic">No candidates qualified — adaptive selector would fall to Tier 3 fallback or return empty.</div>
            ) : (
              <div className="space-y-2">
                {topPickCandidates.map((ins, i) => (
                  <div key={ins.public_id} className="border border-neutral-800 rounded p-3 bg-neutral-950">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="text-sm text-neutral-200 font-medium truncate">
                        <span className="text-neutral-500 font-mono mr-2">#{i + 1}</span>
                        {ins.paper_title}
                      </div>
                      <div className="text-[11px] text-neutral-500 font-mono shrink-0">
                        fidelity={ins.fidelity_score?.toFixed(2)} · {ins.classification}
                      </div>
                    </div>
                    {ins.curation_verdict && (
                      <div className="mt-2 text-[11px] text-neutral-500">
                        verdict: <span className="font-mono text-emerald-400">{ins.curation_verdict.decision}</span>
                        <span className="ml-2">conf: <span className="font-mono">{ins.curation_verdict.confidence?.toFixed(2)}</span></span>
                        <span className="ml-2">gates: <span className="font-mono">[{ins.curation_verdict.gates_passed?.join(', ')}]</span></span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* v3.3 — weekly summary preview (test mode only). Renders the
          intro + themes + each top-pick's headline/narrative/brief. This is
          what the prod email body would look like if you Promote with
          "Keep this intro on prod" checked. */}
      {activeJob?.status === 'completed' && activeJob.is_test_run && summaryPreview?.content && (
        <div className="border border-violet-700/60 rounded-lg p-4 bg-violet-950/20">
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="text-base font-semibold text-violet-200">
              Email preview · weekly letter
            </h3>
            <span className="text-[10px] uppercase tracking-wide font-mono text-violet-400">
              draft · ~what prod would publish
            </span>
          </div>

          {summaryPreview.content.intro && (
            <div className="mb-4 p-3 bg-neutral-950 border border-neutral-800 rounded">
              <div className="text-[11px] uppercase tracking-wide text-violet-400 mb-2">Intro</div>
              <div className="text-sm text-neutral-200 whitespace-pre-wrap leading-relaxed">
                {summaryPreview.content.intro}
              </div>
            </div>
          )}

          {Array.isArray(summaryPreview.content.themes) && summaryPreview.content.themes.length > 0 && (
            <div className="mb-4">
              <div className="text-[11px] uppercase tracking-wide text-violet-400 mb-2">Themes</div>
              <ul className="space-y-1.5">
                {summaryPreview.content.themes.map((t: any, i: number) => (
                  <li key={i} className="text-sm text-neutral-300">
                    {typeof t === 'string' ? t : (t?.label || JSON.stringify(t))}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {Array.isArray(summaryPreview.content.top_picks) && summaryPreview.content.top_picks.length > 0 && (
            <div className="mb-2">
              <div className="text-[11px] uppercase tracking-wide text-violet-400 mb-2">
                Top picks ({summaryPreview.content.top_picks.length})
              </div>
              <div className="space-y-3">
                {summaryPreview.content.top_picks.map((pick: any, i: number) => (
                  <div key={pick.insight_public_id || i} className="p-3 bg-neutral-950 border border-neutral-800 rounded">
                    <div className="text-sm font-medium text-neutral-100">{pick.headline}</div>
                    {pick.narrative_md && (
                      <div className="mt-2 text-[13px] text-neutral-300 whitespace-pre-wrap leading-relaxed">
                        {pick.narrative_md}
                      </div>
                    )}
                    {pick.infographic_brief && (
                      <div className="mt-2 text-[11px] text-neutral-500 italic">
                        Infographic brief: {pick.infographic_brief}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {summaryPreview.content.closing_nudge && (
            <div className="mt-3 p-3 bg-neutral-950 border border-neutral-800 rounded">
              <div className="text-[11px] uppercase tracking-wide text-violet-400 mb-2">Closing</div>
              <div className="text-sm text-neutral-300 whitespace-pre-wrap">{summaryPreview.content.closing_nudge}</div>
            </div>
          )}

          <p className="text-[11px] text-neutral-500 mt-3">
            Generated against test-week candidates (status=draft OR published, high/medium relevance).
            Prod summary may differ if prod's sources surface different papers — checking "Keep this intro on prod" copies this content with public_ids remapped by paper_url.
          </p>
        </div>
      )}

      {activeJob?.status === 'completed' && activeJob.is_test_run && !summaryPreview?.content && insights.length > 0 && (
        <div className="border border-amber-700/60 rounded-lg p-4 bg-amber-950/20 text-sm text-amber-200">
          <strong>No email preview generated.</strong>{' '}
          <span className="text-amber-100/70">
            Either Bedrock failed or there were 0 high/medium-relevance candidates in the test sample.
            Promote will regenerate freshly on prod regardless. (Bedrock error logs are on the backend.)
          </span>
        </div>
      )}

      {/* Recent runs list */}
      <div className="border border-neutral-800 rounded-lg p-4 bg-neutral-900/40">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-neutral-200">Recent runs (last 15)</h3>
          <button
            onClick={cleanupTestData}
            disabled={busy !== 'idle'}
            className="text-[11px] text-neutral-500 hover:text-neutral-300 disabled:opacity-50"
            title="Hard-delete test runs + their orphan rows older than 24h. Prod data untouched."
          >
            {busy === 'cleaning' ? 'cleaning…' : 'cleanup test data >24h'}
          </button>
        </div>
        {recentJobs.length === 0 ? (
          <div className="text-xs text-neutral-500">No discovery runs yet.</div>
        ) : (
          <div className="space-y-1.5">
            {recentJobs.map(j => (
              <div key={j.public_id}
                onClick={() => { setActiveJobPid(j.public_id); setActiveJob(j); setInsights([]); }}
                className={`p-2 rounded border cursor-pointer hover:bg-neutral-950 ${
                  j.public_id === activeJobPid ? 'border-indigo-500 bg-indigo-900/10' : 'border-neutral-800'
                }`}
              >
                <div className="flex items-baseline justify-between gap-2 text-xs">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${
                      j.is_test_run ? 'bg-amber-900/40 text-amber-300' : 'bg-emerald-900/40 text-emerald-300'
                    }`}>
                      {j.is_test_run ? `TEST n=${j.sample_size}` : 'PROD'}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${
                      j.status === 'completed' ? 'bg-emerald-900/30 text-emerald-300' :
                      j.status === 'failed' ? 'bg-red-900/30 text-red-300' :
                      'bg-indigo-900/30 text-indigo-300'
                    }`}>
                      {j.status}
                    </span>
                    <span className="font-mono text-neutral-400 truncate">{j.week_iso}</span>
                    <span className="text-neutral-600">·</span>
                    <span className="text-neutral-400">{j.sources?.join(',')}</span>
                  </div>
                  <div className="text-neutral-500 font-mono shrink-0">
                    {j.papers_total} papers · {j.created_at?.slice(0, 16)}
                  </div>
                </div>
                {j.parent_job_public_id && (
                  <div className="text-[10px] text-neutral-600 mt-1 ml-1">
                    promoted from test <span className="font-mono">{j.parent_job_public_id.slice(0, 8)}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BucketCell(props: { label: string; count: number; color: string; total: number }) {
  const pct = props.total > 0 ? Math.round((props.count / props.total) * 100) : 0;
  const colorClasses: Record<string, string> = {
    emerald: 'border-emerald-700 bg-emerald-900/20 text-emerald-300',
    amber: 'border-amber-700 bg-amber-900/20 text-amber-300',
    indigo: 'border-indigo-700 bg-indigo-900/20 text-indigo-300',
    rose: 'border-rose-700 bg-rose-900/20 text-rose-300',
  };
  return (
    <div className={`p-3 border rounded ${colorClasses[props.color] || colorClasses.indigo}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-80">{props.label}</div>
      <div className="text-2xl font-semibold mt-1">{props.count}</div>
      <div className="text-[11px] opacity-70 font-mono">{pct}% of {props.total}</div>
    </div>
  );
}
